/* ============================================================================
   Custom Napkin Designer — v2
   UI4 functionality, Five Star Napkins Brand Guidelines v3.0 styling.
   Vanilla JS, no framework, no Fabric. Custom DOM layer editor + an offscreen
   <canvas> export that share one geometry module (NKD.geo) — so the proof the
   customer approves IS the file that gets exported (preview === print).

   Reuses v1 plumbing: heic2any (HEIC decode), PDF.js (rasterise vector uploads
   for preview), jsPDF (print PDF), Azure Blob SAS upload, atomic /cart/add.js items[].
   Pricing display reads assets/napkin-pricing-config.json; per BUILD-PLAN-2026-05-20
   native Shopify automatic discounts are authoritative for what's actually charged.
   ========================================================================== */
(function () {
  "use strict";

  /* ------------------------------------------------------------------ utils */
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) =>
    Array.prototype.slice.call((root || document).querySelectorAll(sel));
  function el(tag, attrs, kids) {
    const node = document.createElement(tag);
    if (attrs)
      for (const k in attrs) {
        const v = attrs[k];
        if (v == null || v === false) continue;
        if (k === "class") node.className = v;
        else if (k === "html") node.innerHTML = v;
        else if (k === "text") node.textContent = v;
        else if (k === "style" && typeof v === "object")
          Object.assign(node.style, v);
        else if (k.slice(0, 2) === "on" && typeof v === "function")
          node.addEventListener(k.slice(2), v);
        else if (k === "dataset" && typeof v === "object")
          for (const d in v) node.dataset[d] = v[d];
        else node.setAttribute(k, v === true ? "" : v);
      }
    if (kids != null)
      (Array.isArray(kids) ? kids : [kids]).forEach((c) => {
        if (c == null || c === false) return;
        node.appendChild(
          typeof c === "string" ? document.createTextNode(c) : c,
        );
      });
    return node;
  }
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), ms);
    };
  }
  function moneyUSD(n) {
    return (
      "$" +
      (Math.round(n * 100) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  /* --------------------------------------------------------------- geometry */
  /* The single source of truth for napkin/imprint dimensions and layer maths.
     - Cocktail napkin: 4.5"x4.5"; printable imprint 4"x4" (0.25" margin all round).
     - 1/4 Fold: 8"x8" sheet folded twice into a 4"x4" quadrant. The two ADJACENT fold
       edges (meeting at FOLD_CORNER) run flush to the cloth (NO margin); the two finished
       edges keep 0.25". Imprint = 3.75"x3.75" pinned into the fold corner.
     - Geometry is four per-edge insets {top,right,bottom,left} in inches; cocktail = BLEED
       on all four (centred), QF = 0 on the two fold edges. The same insets travel to the
       backend engine via vppPlacement so preview, proof, and print match by construction.
     - Diamond = the napkin (and the export) rotated 45deg. Insets are defined ONCE in cloth
       frame (pre-rotation); diamond is pure CSS/box rotation on top, no special-casing.
     - Layer BOXES are stored as FRACTIONS of the imprint area:
         { x, y, w, h }   each 0..1, top-left + size relative to the imprint
         rotation         degrees
         fontFrac         (text only) font size as a fraction of the imprint height
       Fractions are resolution-independent → the on-screen editor and the export
       canvas render the identical picture by construction. */
  const NKD = (window.NKD = window.NKD || {});
  NKD.geo = {
    EXPORT_DPI: 600,
    PROOF_PX: 1200, // proof PNG side (napkin square at ~266 DPI for a 4.5" napkin — plenty for screen approval)
    BLEED_IN: 0.25, // non-printable margin on a FINISHED/open edge
    // QF folds into a quadrant; the two adjacent fold edges meet at this corner and
    // print flush to the cloth (zero margin). The two finished edges keep BLEED_IN.
    // Cloth-frame constant (pre-diamond-rotation), QF only; cocktail is symmetric.
    // SPEC GATE: confirm with Paper Place which physical corner the napkin folds from.
    FOLD_CORNER: "top-left",
    // The two edges adjacent to a given corner (its fold edges).
    foldEdges(corner) {
      const c = corner || "top-left";
      return {
        top: c === "top-left" || c === "top-right",
        bottom: c === "bottom-left" || c === "bottom-right",
        left: c === "top-left" || c === "bottom-left",
        right: c === "top-right" || c === "bottom-right",
      };
    },
    // Per-edge non-printable inset in inches { top, right, bottom, left }.
    // Cocktail = BLEED on all four. QF = 0 on the two fold edges, BLEED on the rest.
    insets(product) {
      const isQF = product && product.group === "1/4 Fold";
      const B = this.BLEED_IN;
      if (!isQF) return { top: B, right: B, bottom: B, left: B };
      const f = this.foldEdges(this.FOLD_CORNER);
      return {
        top: f.top ? 0 : B,
        right: f.right ? 0 : B,
        bottom: f.bottom ? 0 : B,
        left: f.left ? 0 : B,
      };
    },
    napkin(product) {
      const isQF = product && product.group === "1/4 Fold";
      const napkinIn = isQF ? 4.0 : 4.5;
      const insets = this.insets(product);
      return {
        isQF,
        napkinIn,
        insets,
        // imprint is square in every current config (one fold + one finished per axis):
        // cocktail 4.0, QF 3.75. Derived from insets so it stays correct if they move.
        imprintIn: napkinIn - insets.left - insets.right,
        foldCorner: isQF ? this.FOLD_CORNER : null,
        diamond: !!(product && product.shape === "diamond"),
      };
    },
    /* On-screen layout for a given napkin display size (px). */
    layout(product, displaySize) {
      const n = this.napkin(product);
      const pxPerIn = displaySize / n.napkinIn;
      const imprintPx = n.imprintIn * pxPerIn;
      return {
        ...n,
        displaySize,
        pxPerIn,
        imprintPx,
        // asymmetric origin: pinned to the fold corner on QF (0,0 for top-left),
        // centred on cocktail (BLEED on each side).
        imprintOffsetX: n.insets.left * pxPerIn,
        imprintOffsetY: n.insets.top * pxPerIn,
      };
    },
    exportPx(product) {
      const n = this.napkin(product);
      return {
        ...n,
        napkinPx: Math.round(n.napkinIn * this.EXPORT_DPI),
        imprintPx: Math.round(n.imprintIn * this.EXPORT_DPI),
      };
    },
  };

  /* ---------------------------------------------------------------- config */
  const CFG = (function () {
    const node = $("#nkd-config");
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.error("[napkin-designer] bad config JSON", e);
      return {};
    }
  })();
  const CONTACT_URL = CFG.contactUrl || "/pages/contact";
  const DESIGN_EMAIL = "design@fivestarnapkins.com"; // contact-us box → mailto target

  /* Pricing config (mirror of repo pricing-config.json). Fetched async; the
     shell renders immediately, price-dependent UI lights up once it lands. */
  let PRICING = null;
  function loadPricing() {
    if (!CFG.pricingConfigUrl)
      return Promise.reject(new Error("no pricingConfigUrl"));
    return fetch(CFG.pricingConfigUrl)
      .then((r) => r.json())
      .then((j) => {
        PRICING = j;
        return j;
      });
  }
  const MOQ = () => (PRICING ? PRICING.moq : 200);
  const QTY_STEP = () => (PRICING ? PRICING.qty_step : 50);
  const QUOTE_AT = () => (PRICING ? PRICING.quote_threshold : 50000);
  const SAMPLE_PRICE = () =>
    PRICING && PRICING.sample ? PRICING.sample.price : 50;
  const SAMPLE_MIN = () =>
    PRICING && PRICING.sample ? PRICING.sample.min_qty : 1000;
  const FOIL_SURCHARGE = () =>
    PRICING && PRICING.foil ? PRICING.foil.surcharge_per_napkin : 0.4;

  function tiersFor(productKey) {
    return (
      (PRICING &&
        PRICING.products &&
        PRICING.products[productKey] &&
        PRICING.products[productKey].tiers) ||
      []
    );
  }
  function unitPriceUSD(productKey, qty) {
    const tiers = tiersFor(productKey);
    let p = tiers.length ? tiers[0].unit_price : 0;
    for (const t of tiers) {
      if (qty >= t.min && (t.max == null || qty <= t.max)) {
        p = t.unit_price;
        break;
      }
    }
    return p;
  }
  function activeTierIndex(productKey, qty) {
    const tiers = tiersFor(productKey);
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (qty >= t.min && (t.max == null || qty <= t.max)) return i;
    }
    return tiers.length ? tiers.length - 1 : -1;
  }
  function nextBreak(productKey, qty) {
    for (const t of tiersFor(productKey)) if (qty < t.min) return t;
    return null;
  }
  function marketCoefficient(countryCode) {
    if (!PRICING || !PRICING.international) return 1;
    const markets = PRICING.international.markets || [];
    let star = PRICING.international.default_coefficient || 1;
    for (const m of markets) {
      const cs = m.countries || [];
      if (cs.indexOf(countryCode) !== -1) return m.coefficient;
      if (cs.indexOf("*") !== -1) star = m.coefficient;
    }
    return star;
  }
  function makeNudge(productKey, qty) {
    const cur = unitPriceUSD(productKey, qty);
    const next = nextBreak(productKey, qty);
    if (!next) return null;
    const savings = Math.round((cur - next.unit_price) * next.min);
    if (savings <= 0) return null;
    return (
      "Order " +
      next.min.toLocaleString() +
      " to drop to " +
      moneyUSD(next.unit_price) +
      " / napkin — save " +
      moneyUSD(savings) +
      "."
    );
  }

  /* ------------------------------------------------------------------ state */
  const State = {
    step: 1, // 1..4; null = all collapsed
    product: {
      id: "cocktail-square",
      group: "Cocktail",
      shape: "square",
      key: "cocktail",
      sizeLabel: "",
    },
    napkinColor: null, // { id, name, hex, sub }
    texts: [], // [{ id, text, font, box }] — one entry per text box; seeded in hydrateData
    font: null, // { family, group, style? } — last-picked font; seeds the next new text box
    logo: null, // { src, name, type, opaque }   (src = data URL / blob URL for preview; type = original ext)
    designColorTab: "default", // default | pantone | custom
    designColor: null, // { id, name, hex }
    pantone: "",
    customHex: "#1c150f",
    foil: false,
    foilColor: null, // { id, name }
    qty: 500,
    sample: false,
    showSafeZone: true,
    showGrid: false, // default OFF (client review 2026-06-13): no aqua on the napkin until asked for
    // Per-step "the customer actually did something here" flags. The completion
    // ticks are gated on these so sensible defaults (product, colour, qty=500)
    // don't show steps as already done on a fresh load.
    touched: {},
    // layer boxes — fractions of the imprint area (see NKD.geo). Lazily initialised.
    // Each text box carries its own box inside its State.texts entry.
    logoBox: null, // { x, y, w, h, rotation }
    selectedLayer: null, // 'logo' | 'text:<id>' | null  (compound layer id)
    rotatingAngle: null, // live angle while a rotate drag is in progress
    termsAcceptedAt: null, // ISO timestamp of when the T&C box was first ticked in the current modal session
  };

  const DEFAULT_TEXT_BOX = {
    x: 0.08,
    y: 0.4,
    w: 0.84,
    h: 0.18,
    rotation: 0,
    fontFrac: 0.12,
  };
  const DEFAULT_LOGO_BOX = { x: 0.27, y: 0.06, w: 0.46, h: 0.26, rotation: 0 };
  const MAX_TEXTS = 2; // max independent text boxes a customer can add — hard 2-line
  // cap matching the "+ Add 2nd line" button (Debra 18 Jun review item #6)
  let textIdSeq = 0; // monotonic source of text-item ids (stable layer identity)

  /* ------------------------------------------------------------------- data */
  const DATA = {
    products: [
      {
        id: "cocktail-square",
        group: "Cocktail",
        shape: "square",
        key: "cocktail",
      },
      {
        id: "cocktail-diamond",
        group: "Cocktail",
        shape: "diamond",
        key: "cocktail",
      },
      {
        id: "qf-square",
        group: "1/4 Fold",
        shape: "square",
        key: "quarter_fold",
      },
      {
        id: "qf-diamond",
        group: "1/4 Fold",
        shape: "diamond",
        key: "quarter_fold",
      },
    ],
    napkinColors: [],
    designColors: [],
    fonts: [],
    foilColors: [
      { id: "gold", name: "Gold" },
      { id: "silver", name: "Silver" },
    ],
    qfColorIds: ["white", "black"],
  };
  function prettySize(s) {
    return String(s)
      .replace(/"/g, "″")
      .replace(/\s*x\s*/gi, " × ");
  }
  function sizeLabelFor(product) {
    const p = PRICING && PRICING.products && PRICING.products[product.key];
    if (!p)
      return product.key === "quarter_fold"
        ? "4″ × 4″ (unfolds to 8″ × 8″)"
        : "4.5″ × 4.5″";
    return prettySize(p.napkin_size);
  }
  function hydrateData() {
    if (!PRICING) return;
    // Back-compat: prefer the new name; fall back to the legacy `_25` key so
    // a stale data file (or a partial deploy) doesn't blank out Step 2.
    DATA.napkinColors =
      PRICING.napkin_colors_cocktail || PRICING.napkin_colors_25 || [];
    DATA.designColors = PRICING.design_colors_default || [];
    DATA.fonts = PRICING.fonts || [];
    if (!State.napkinColor && DATA.napkinColors.length)
      State.napkinColor = DATA.napkinColors[0];
    if (!State.designColor && DATA.designColors.length)
      State.designColor = DATA.designColors[0];
    if (!State.font && DATA.fonts.length) State.font = DATA.fonts[0];
    if (!State.texts.length) State.texts = [makeTextItem(0)];
    if (!State.foilColor) State.foilColor = DATA.foilColors[0];
    State.product.sizeLabel = sizeLabelFor(State.product);
    State.qty = clamp(State.qty, MOQ(), 10000000);
  }
  function napkinColorOptions() {
    return State.product.key === "quarter_fold"
      ? DATA.napkinColors.filter((c) => DATA.qfColorIds.indexOf(c.id) !== -1)
      : DATA.napkinColors;
  }
  /* Napkin photography (FSN-supplied product photos, 2 Jul 2026). Each colour in the
     pricing config carries `photo` / `photo_chip` theme-asset filenames. URLs derive
     from pricingConfigUrl: every designer asset lives in the same theme /assets/ dir,
     so no extra Liquid plumbing is needed. Unversioned asset URLs always serve the
     theme's current copy. Colours without a photo fall back to the flat hex. */
  const ASSET_BASE = (CFG.pricingConfigUrl || "")
    .split("?")[0]
    .replace(/[^/]+$/, "");
  function napkinPhotoUrl(c, chip) {
    const f = c && (chip ? c.photo_chip : c.photo);
    return f && ASSET_BASE ? ASSET_BASE + f : null;
  }
  // CSS background value: the photo layered over the flat hex (hex shows while the
  // image loads, and stands in entirely when no photo exists).
  function napkinSurfaceCSS(c, chip) {
    const hex = (c && c.hex) || "#fff";
    const u = napkinPhotoUrl(c, chip);
    return u ? 'url("' + u + '") center / cover no-repeat, ' + hex : hex;
  }
  // Ink colour currently in effect (null when foil is on → use a foil gradient instead).
  function inkColorHex() {
    if (State.foil) return null;
    if (State.designColorTab === "pantone") return null; // pantone shows as a swatch-less code; preview falls back to ink default
    if (State.designColorTab === "custom") return State.customHex || "#1c150f";
    return (State.designColor && State.designColor.hex) || "#111111";
  }
  function previewInkHex() {
    // For the preview/proof we need *some* colour even for Pantone (we can't know the real ink) — show near-black.
    return (
      inkColorHex() ||
      (State.designColorTab === "pantone" ? "#1c150f" : "#1c150f")
    );
  }
  function foilGradientCSS(which) {
    return which === "silver"
      ? "linear-gradient(135deg,#e8e8e8 0%,#a8a8a8 50%,#e8e8e8 100%)"
      : "linear-gradient(135deg,#ead489 0%,#cda64f 50%,#ead489 100%)";
  }
  function designColourLabel() {
    if (State.foil)
      return "Foil — " + ((State.foilColor && State.foilColor.name) || "Gold");
    if (State.designColorTab === "pantone")
      return State.pantone
        ? "Pantone " + State.pantone
        : "Pantone (to confirm)";
    if (State.designColorTab === "custom")
      return "Custom " + (State.customHex || "");
    return (State.designColor && State.designColor.name) || "Black";
  }

  /* ------------------------------------------------------------------ icons */
  const ICONS = {
    check:
      '<svg viewBox="0 0 13 13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 7l3 3L11 3"/></svg>',
    rotate:
      '<svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><path d="M2 5a3 3 0 1 0 .8-2"/><path d="M2 2v1.6h1.6"/></svg>',
  };

  /* ------------------------------------------------------------------ toast */
  let toastEl, toastTimer;
  function toast(msg, kind) {
    if (!toastEl) {
      toastEl = el("div", { class: "nkd__toast", "aria-atomic": "true" });
      document.body.appendChild(toastEl);
    }
    // a11y: errors must interrupt (assertive/alert); info is polite (status).
    toastEl.setAttribute("role", kind === "error" ? "alert" : "status");
    toastEl.setAttribute(
      "aria-live",
      kind === "error" ? "assertive" : "polite",
    );
    toastEl.textContent = msg;
    toastEl.setAttribute("data-kind", kind || "info");
    toastEl.setAttribute("data-show", "");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(
      () => toastEl.removeAttribute("data-show"),
      kind === "error" ? 6000 : 3500,
    );
  }

  /* ============================================================== rendering */
  const STEPS = [
    { num: 1, title: "Product" },
    { num: 2, title: "Color" },
    { num: 3, title: "Customize" },
    { num: 4, title: "Order" },
  ];
  const ROOT = $("#napkin-designer-v2");
  let dom = {}; // node refs filled by renderShell()

  function renderShell() {
    ROOT.setAttribute("data-nkd-hydrated", "");
    ROOT.innerHTML = "";

    /* left rail */
    const railHead = el("div", { class: "nkd__rail-head" }, [
      el("h2", { text: "Design your napkins" }),
      el("p", {
        text: "100% organic cotton · printed to order",
      }),
    ]);
    const stepNodes = STEPS.map((s) => {
      const header = el(
        "button",
        {
          class: "nkd__step-header",
          type: "button",
          "aria-expanded": "false",
          onclick: () => {
            State.step = State.step === s.num ? null : s.num;
            refreshSteps();
          },
        },
        el("span", { class: "nkd__step-pill" }, [
          el(
            "span",
            { class: "nkd__step-num", "data-num": s.num },
            String(s.num),
          ),
          el("span", { class: "nkd__step-title", text: s.title }),
        ]),
      );
      const body = el("div", {
        class: "nkd__step-body",
        "data-step-body": s.num,
      });
      const wrap = el("div", { class: "nkd__step", "data-step": s.num }, [
        header,
        body,
      ]);
      return { wrap, header, body, num: s.num };
    });
    const rail = el("aside", { class: "nkd__rail" }, [
      railHead,
      ...stepNodes.map((n) => n.wrap),
    ]);

    /* centre stage — live preview.
       Imprint is a CHILD of the napkin so it rotates with the napkin (the imprint is a
       physical area on the napkin sheet — 4" centered on a 4.5" cocktail sheet — so it
       MUST rotate with the napkin or the geometry breaks). The grid is moved out to be a
       wrap-level overlay (separate code path in paintPreview) so NSEW stays axis-aligned.
       New layers default to rotation = -napkin_rotation (in imprint frame) so they appear
       axis-aligned to the viewer despite the imprint frame being rotated. */
    const napkin = el("div", {
      class: "nkd__napkin",
      "data-shape": State.product.shape,
    });
    const imprint = el("div", { class: "nkd__imprint" });
    napkin.appendChild(imprint);
    const previewWrap = el("div", { class: "nkd__preview-wrap" }, [napkin]);
    const stageEyebrow = el("div", {
      class: "nkd__stage-eyebrow nkd-uc",
      text: "",
    });
    const caption = el("div", { class: "nkd__stage-caption" }, [
      el("span", { class: "nkd__cap-label", text: "Napkin color" }),
      el("span", { class: "nkd__cap-value" }, [
        el("span", { "data-cap-name": "", text: "—" }),
        el("span", { class: "nkd__cap-sub", "data-cap-sub": "", text: "" }),
      ]),
    ]);
    const safeToggle = el(
      "button",
      {
        // --safe gives the toggle's indicator box a cross-hatch swatch matching the napkin's
        // safe zone (Debra 18 Jun review #2: "Safe zone box should show cross hatching").
        class: "nkd__tool nkd__tool--safe",
        type: "button",
        "aria-pressed": String(State.showSafeZone),
        onclick: () => {
          if (safeToggle.disabled) return;
          State.showSafeZone = !State.showSafeZone;
          safeToggle.setAttribute("aria-pressed", String(State.showSafeZone));
          paintPreview();
        },
      },
      "Safe zone",
    );
    const gridToggle = el(
      "button",
      {
        class: "nkd__tool",
        type: "button",
        "aria-pressed": String(State.showGrid),
        onclick: () => {
          State.showGrid = !State.showGrid;
          gridToggle.setAttribute("aria-pressed", String(State.showGrid));
          paintPreview();
        },
      },
      "Alignment grid",
    );
    const tools = el("div", { class: "nkd__tools" }, [safeToggle, gridToggle]);
    const stage = el("main", { class: "nkd__stage" }, [
      stageEyebrow,
      previewWrap,
      caption,
      tools,
    ]);

    /* modal + offscreen export canvas */
    const modal = buildModal();
    const exportCanvas = el("canvas", { class: "nkd__export-canvas" });

    ROOT.appendChild(el("div", { class: "nkd__app" }, [rail, stage]));
    ROOT.appendChild(modal.node);
    ROOT.appendChild(exportCanvas);
    ROOT.appendChild(renderContactBox());

    dom = {
      rail,
      stage,
      steps: stepNodes,
      previewWrap,
      napkin,
      imprint,
      stageEyebrow,
      capName: $("[data-cap-name]", caption),
      capSub: $("[data-cap-sub]", caption),
      safeToggle,
      gridToggle,
      modal,
      exportCanvas,
      layerEls: {}, // layer els keyed by layer id ("logo" | "text:<id>")
      anglePill: null,
    };

    // deselect layer when clicking outside any layer
    document.addEventListener("pointerdown", (e) => {
      if (!State.selectedLayer) return;
      const inLayer = e.target.closest && e.target.closest(".nkd__layer");
      if (!inLayer) {
        State.selectedLayer = null;
        renderLayers();
      }
    });
    // close any open font menu when clicking outside a font picker. One
    // delegated handler — replaces the per-render document listener the old
    // single-font UI added on every Step-3 render (which leaked one per render).
    document.addEventListener("pointerdown", (e) => {
      if (!(e.target.closest && e.target.closest(".nkd__font-select")))
        closeAllFontMenus();
    });

    refreshSteps();
    paintPreview();
  }

  /* ----------------------------------------------- contact-us box (mailto)
     A collapsed "?" button bottom-right of the designer; clicking it pops a
     small help panel (close via ×, outside-click, or Escape). The panel's
     button builds the mailto: just-in-time on click, so the pre-filled body
     always reflects the customer's CURRENT design. No in-page form — a
     templated mailto body can't lose a typed message or blow the URL length
     limit, and degrades to a no-op (vs an error) if the customer has no mail
     client. The visible address below is the copy-it fallback. */
  function buildContactMailto() {
    const p = State.product || {};
    const shape = p.shape === "diamond" ? "Diamond" : "Square";
    const lines = [
      "Hi Five Star Napkins,",
      "",
      "[Tell us how we can help with your napkin design.]",
      "",
      "— My design so far —",
      "Product: " + (p.group || "Cocktail") + " · " + shape,
    ];
    if (State.napkinColor && State.napkinColor.name)
      lines.push("Napkin colour: " + State.napkinColor.name);
    lines.push("Quantity: " + Number(State.qty || 0).toLocaleString());
    const texts = (State.texts || [])
      .map((t) => t && t.text && t.text.trim())
      .filter(Boolean);
    if (texts.length)
      lines.push("Text: " + texts.map((t) => '"' + t + '"').join(", "));
    return (
      "mailto:" +
      DESIGN_EMAIL +
      "?subject=" +
      encodeURIComponent("Napkin designer — help with my design") +
      "&body=" +
      encodeURIComponent(lines.join("\r\n"))
    );
  }

  function renderContactBox() {
    const closeBtn = el("button", {
      class: "nkd__contact-close",
      type: "button",
      "aria-label": "Close",
      text: "×",
    });
    const panel = el(
      "div",
      { class: "nkd__contact-panel", id: "nkd-contact-panel" },
      [
        closeBtn,
        el("div", { class: "nkd__contact-title", text: "Need a hand?" }),
        el("p", {
          class: "nkd__contact-copy",
          text: "Questions about your design? We're here to help.",
        }),
        el(
          "button",
          {
            class: "nkd__btn nkd__btn--primary nkd__contact-btn",
            type: "button",
            onclick: () => {
              window.location.href = buildContactMailto();
            },
          },
          "Email us",
        ),
        el("div", { class: "nkd__contact-alt" }, [
          "or email ",
          el("a", { href: "mailto:" + DESIGN_EMAIL, text: DESIGN_EMAIL }),
        ]),
      ],
    );
    const toggle = el("button", {
      class: "nkd__contact-toggle",
      type: "button",
      "aria-label": "Need help?",
      "aria-expanded": "false",
      "aria-controls": "nkd-contact-panel",
      text: "?",
    });
    const wrap = el("aside", { class: "nkd__contact" }, [toggle, panel]);
    function setOpen(open) {
      if (open) wrap.setAttribute("data-open", "");
      else wrap.removeAttribute("data-open");
      toggle.setAttribute("aria-expanded", String(open));
    }
    toggle.addEventListener("click", () => setOpen(true));
    closeBtn.addEventListener("click", () => setOpen(false));
    // close on outside-click / Escape — registered once (renderShell runs once)
    document.addEventListener("pointerdown", (e) => {
      if (wrap.hasAttribute("data-open") && !wrap.contains(e.target))
        setOpen(false);
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && wrap.hasAttribute("data-open")) setOpen(false);
    });
    return wrap;
  }

  function computeCompleted() {
    return {
      1: true, // a product is always selected
      2: !!State.napkinColor,
      3: !!(State.texts.some((t) => t.text.trim()) || State.logo),
      4: State.qty >= MOQ(),
    };
  }
  function refreshSteps() {
    const completed = computeCompleted();
    dom.steps.forEach((n) => {
      const active = State.step === n.num;
      // A step only shows its tick once the customer has actually interacted
      // with it (or moved past it via Next Step) — defaults alone don't count.
      const done = !!completed[n.num] && !!State.touched[n.num] && !active;
      n.wrap.toggleAttribute("data-active", active);
      n.wrap.toggleAttribute("data-complete", done);
      n.header.setAttribute("aria-expanded", String(active));
      const numEl = $(".nkd__step-num", n.header);
      numEl.innerHTML = done
        ? '<svg viewBox="0 0 13 13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 7l3 3L11 3"/></svg>'
        : String(n.num);
      if (active) renderStepBody(n.num, n.body);
      else n.body.innerHTML = "";
    });
  }
  /* Navigation is fully manual: the customer moves between steps at their own
     pace via the step headers or the explicit Next Step button at the bottom of
     steps 1–3. (The earlier auto-advance was removed at FSN's request.) */
  function goToStep(next) {
    if (next > STEPS.length || next < 1) return;
    State.step = next;
    refreshSteps();
    const s = dom.steps.find((n) => n.num === next);
    if (s) {
      try {
        s.wrap.scrollIntoView({ behavior: "smooth", block: "nearest" });
        s.header.focus({ preventScroll: true });
      } catch (_) {
        /* older browsers: no smooth scroll, non-essential */
      }
    }
  }
  function appendStepNav(body, num) {
    // Last step (Order) gets a Back button but no "Next Step" — the action there is
    // Add to cart, not advance. Steps 1–3 get Next; steps 2–4 get Back.
    const isLast = num >= STEPS.length;
    body.appendChild(
      el("div", { class: "nkd__step-nav" }, [
        // Back / previous-step button (Debra 18 Jun review items #4/#12). Only from
        // step 2+ (step 1 has nowhere to go back to). Preserves ALL customer work:
        // goToStep mutates only State.step, never product/color/texts/logo/qty, and
        // Back deliberately does NOT set State.touched (so it can't falsely tick a
        // step complete). In-wizard nav only — not browser-back / reload survival.
        num > 1 &&
          el("button", {
            class: "nkd__step-back",
            type: "button",
            text: "Previous Step",
            onclick: () => goToStep(num - 1),
          }),
        !isLast &&
          el("button", {
            class: "nkd__step-next",
            type: "button",
            text: "Next Step",
            onclick: () => {
              // moving on counts as completing the step (defaults accepted)
              State.touched[num] = true;
              goToStep(num + 1);
            },
          }),
      ]),
    );
  }
  function renderStepBody(num, body) {
    body.innerHTML = "";
    if (num === 1) renderStepProduct(body);
    else if (num === 2) renderStepColor(body);
    else if (num === 3) renderStepCustomize(body);
    else if (num === 4) renderStepOrder(body);
    // Every step gets a nav row now: steps 1–3 show Next (+ Back from 2), step 4 (Order)
    // shows Back only. appendStepNav decides which buttons to render per step.
    appendStepNav(body, num);
  }
  function refreshCurrentStepBody() {
    const s = dom.steps.find((n) => n.num === State.step);
    if (s) renderStepBody(s.num, s.body);
  }

  /* ----------------------------------------------------------- Step 1 — Product */
  function renderStepProduct(body) {
    const groups = [
      {
        name: "Cocktail",
        items: DATA.products.filter((p) => p.group === "Cocktail"),
      },
      {
        name: "1/4 Fold",
        items: DATA.products.filter((p) => p.group === "1/4 Fold"),
      },
    ];
    groups.forEach((g) => {
      const cards = g.items.map((p) => {
        const sel = State.product.id === p.id;
        return el(
          "button",
          {
            class: "nkd__product-card",
            type: "button",
            "aria-pressed": String(sel),
            onclick: () => selectProduct(p),
          },
          [
            el(
              "span",
              {
                class:
                  "nkd__product-card-ico" +
                  (p.shape === "diamond"
                    ? " nkd__product-card-ico--diamond"
                    : ""),
              },
              el("i"),
            ),
            el("span", {
              class: "nkd__product-card-name",
              text: p.shape === "square" ? "Square" : "Diamond",
            }),
            el("span", {
              class: "nkd__product-card-size",
              text: sizeLabelFor(p),
            }),
          ],
        );
      });
      body.appendChild(
        el("div", { class: "nkd__product-grp" }, [
          el("div", { class: "nkd__product-grp-name", text: g.name }),
          el("div", { class: "nkd__product-cards" }, cards),
        ]),
      );
    });
  }
  function selectProduct(p) {
    const wasKey = State.product.key;
    const wasShape = State.product.shape;
    State.product = { ...p, sizeLabel: sizeLabelFor(p) };
    // Shape change → preserve each existing layer's on-screen angle. Without
    // this, switching from square to diamond (or vice-versa) makes layers
    // rotate visibly with the napkin, which is technically WYSIWYG-on-table
    // but feels surprising. Storage convention: box.rotation is in imprint
    // frame; visible angle = box.rotation + napkinRotation(). To keep the
    // visible angle constant, subtract the napkinRotation delta.
    if (wasShape !== p.shape) {
      const delta =
        (p.shape === "diamond" ? 45 : 0) - (wasShape === "diamond" ? 45 : 0);
      State.texts.forEach((t) => {
        if (t.box) t.box.rotation -= delta;
      });
      if (State.logoBox) State.logoBox.rotation -= delta;
    }
    // QF → force napkin colour into the allowed set
    if (
      State.product.key === "quarter_fold" &&
      State.napkinColor &&
      DATA.qfColorIds.indexOf(State.napkinColor.id) === -1
    ) {
      State.napkinColor = napkinColorOptions()[0] || State.napkinColor;
    }
    // QF safe-zone is the real 0.25" non-printable margin between the 3.5" imprint and the
    // 4" napkin edge — same as cocktail (NKD.geo: imprintIn = napkinIn − 2×BLEED_IN).
    // → no special-casing here; State.showSafeZone is preserved across product switches.
    if (wasKey !== State.product.key) {
      // clamp qty into the new product's tier domain (MOQ unchanged, but be safe)
      State.qty = Math.max(MOQ(), State.qty);
      if (State.qty < SAMPLE_MIN() && State.sample) State.sample = false;
    }
    State.touched[1] = true; // a real pick, not just the default
    refreshSteps(); // re-render Step 1 (selection), and Step 2/4 if open
    paintPreview();
  }

  /* ------------------------------------------------------------ Step 2 — Colour */
  function renderStepColor(body) {
    const opts = napkinColorOptions();
    const isQF = State.product.key === "quarter_fold";
    if (isQF) {
      body.appendChild(
        el("p", {
          class: "nkd__note",
          text: "1/4 Fold napkins are available in Black or White only.",
        }),
      );
    }
    const grid = el("div", {
      class:
        "nkd__swatches " + (isQF ? "nkd__swatches--2" : "nkd__swatches--5"),
    });
    opts.forEach((c) => {
      const sel = State.napkinColor && State.napkinColor.id === c.id;
      grid.appendChild(
        el("button", {
          class: "nkd__swatch",
          type: "button",
          title: c.name,
          "aria-pressed": String(!!sel),
          // FSN's own product photo as the chip, flat hex underneath as fallback
          style: { background: napkinSurfaceCSS(c, true) },
          onclick: () => {
            State.napkinColor = c;
            State.touched[2] = true;
            refreshSteps();
            paintPreview();
          },
        }),
      );
    });
    body.appendChild(grid);
    if (State.napkinColor) {
      body.appendChild(
        el("div", { class: "nkd__colour-readout" }, [
          el("strong", { text: State.napkinColor.name }),
          " ",
          el("span", { text: "— " + (State.napkinColor.sub || "") }),
        ]),
      );
    }
  }

  /* --------------------------------------------------------- Step 3 — Customise */
  /* ---------------------------------------------------- text-box helpers (V2.1) */
  // Each text box is an independent item { id, text, font, box }. Layer identity
  // for the drag/select machinery is the compound id "text:<id>" (vs "logo").
  function makeTextItem(index) {
    const i = index || 0;
    return {
      id: textIdSeq++,
      text: "",
      // each box carries its own font; seeded from the last-picked font
      font: State.font || DATA.fonts[0] || { family: "Work Sans" },
      // Compensate for diamond products: the napkin CSS-rotates +45° in the
      // editor, so a layer stored at rotation 0 would appear visually at 45°.
      // Storing -napkinRotation() makes the layer compound with the napkin to
      // land at 0° on screen. Square products: napkinRotation is 0 → no-op.
      // y is staggered per box by a FULL box-height so a 2nd line lands clear of the
      // first. A smaller 0.09 stagger left the two boxes overlapping, so their (touch-
      // inflated 44px) resize handles collided and grabbing the 2nd box's handle was
      // unreliable — it "didn't resize the same" as the first.
      box: {
        ...DEFAULT_TEXT_BOX,
        y: clamp(
          DEFAULT_TEXT_BOX.y + DEFAULT_TEXT_BOX.h * i,
          0,
          1 - DEFAULT_TEXT_BOX.h,
        ),
        rotation: -napkinRotation(),
      },
    };
  }
  function closeAllFontMenus() {
    $$(".nkd__font-menu").forEach((m) => {
      m.hidden = true;
      // disconnect the per-open IntersectionObserver so Step-3 re-renders (which
      // destroy menu rows) can't leak observers — see lazy-font block below.
      if (m.__fontObs) {
        m.__fontObs.disconnect();
        m.__fontObs = null;
      }
    });
  }

  /* ------------------------------------------------ lazy font loading (V2.2)
     The ~100 customer design fonts are NOT in the page's eager <link> (that
     carries brand chrome only). Each design font is fetched from Google Fonts
     on demand: when its row scrolls into the font menu, when it's picked, and
     — belt-and-braces — for every font a text box uses, awaited before an
     export render. A canvas (proof PNG / print PDF) silently rasterises the
     fallback face AND wraps text on fallback metrics (ctx.measureText) if the
     real font isn't loaded at draw time, so loadFont() is awaited before every
     export renderToCanvas. */
  const fontPromises = {}; // family -> Promise (dedup; resolves when face ready)
  function googleFontHref(font) {
    const fam = String(font.family).trim().replace(/\s+/g, "+");
    // italic-by-design faces (e.g. Cormorant Garamond) need the ital axis
    const axis = font.style === "italic" ? ":ital,wght@1,400" : "";
    return (
      "https://fonts.googleapis.com/css2?family=" + fam + axis + "&display=swap"
    );
  }
  function loadFont(font) {
    if (!font || !font.family) return Promise.resolve();
    const fam = font.family;
    if (fontPromises[fam]) return fontPromises[fam];
    let linkReady;
    try {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      // attach handlers BEFORE href/append so a cache-fast load can't beat them
      linkReady = new Promise((res) => {
        link.onload = res;
        link.onerror = res; // a failed font must not hang add-to-cart
      });
      link.href = googleFontHref(font);
      document.head.appendChild(link);
    } catch (e) {
      linkReady = Promise.resolve();
    }
    const spec =
      (font.style === "italic" ? "italic " : "") + '1em "' + fam + '"';
    const p = linkReady
      .then(() =>
        document.fonts && document.fonts.load
          ? document.fonts.load(spec)
          : null,
      )
      .catch(() => {}); // never reject — degrade gracefully to the serif fallback
    fontPromises[fam] = p;
    return p;
  }
  function ensureFontsLoaded(fonts) {
    return Promise.all((fonts || []).map(loadFont));
  }
  // Fonts used by every text box that actually carries text — the set an export
  // render must have loaded so the proof/print files (and wrapText line metrics)
  // are faithful to the editor.
  function usedTextFonts() {
    const out = [],
      seen = {};
    State.texts.forEach((t) => {
      const fam = t.font && t.font.family;
      if (t.text && t.text.trim() && fam && !seen[fam]) {
        seen[fam] = 1;
        out.push(t.font);
      }
    });
    return out;
  }
  // Build one Step-3 text row: [ text input | × ] then [ font picker | char count ].
  function makeTextRow(item) {
    const row = el("div", { class: "nkd__text-row" });
    const charCount = el("div", {
      class: "nkd__char-count",
      text: item.text.length + " / 100",
    });
    const input = el("input", {
      class: "nkd__input",
      type: "text",
      maxlength: "100",
      value: item.text,
      placeholder: "Add text to your napkin",
      oninput: (e) => {
        item.text = e.target.value.slice(0, 100);
        charCount.textContent = item.text.length + " / 100";
        State.touched[3] = true;
        // No refreshSteps() here — it rebuilds the active step body and would
        // drop input focus mid-keystroke. The step-3 complete flag refreshes on
        // the next step navigation, matching the original single-input UX.
        paintPreview();
      },
    });
    const del = el("button", {
      class: "nkd__text-row-del",
      type: "button",
      "aria-label": "Delete this text box",
      text: "×",
      onclick: () => removeText(item.id),
    });
    /* per-row font picker, bound to this item's font */
    const fontTrigger = el(
      "button",
      { class: "nkd__font-trigger", type: "button" },
      [
        el("span", { "data-font-label": "" }),
        el("span", { class: "nkd__font-caret", text: "▾" }),
      ],
    );
    const fontMenu = el("div", { class: "nkd__font-menu", hidden: true });
    const fontSelect = el("div", { class: "nkd__font-select" }, [
      fontTrigger,
      fontMenu,
    ]);
    const setFontLabel = () => {
      const f = item.font || DATA.fonts[0] || { family: "Work Sans" };
      const lbl = $("[data-font-label]", fontTrigger);
      lbl.textContent = f.family;
      lbl.style.fontFamily = '"' + f.family + '", serif';
      lbl.style.fontStyle = f.style || "normal";
    };
    // Rebuilt on each open (≈100 nodes, <1ms) — keeps the filter + aria-selected
    // state fresh and avoids a stale-cache bug class.
    const buildFontMenu = () => {
      fontMenu.innerHTML = "";
      // sticky typeahead — a flat 100-row scroll is unusable without it
      const filter = el("input", {
        class: "nkd__font-filter",
        type: "text",
        placeholder: "Filter fonts…",
        "aria-label": "Filter fonts",
      });
      fontMenu.appendChild(filter);

      // IntersectionObserver loads each font as its row scrolls into view, so the
      // menu stays WYSIWYG without fetching all ~100. Created per open; stashed on
      // the menu node and disconnected by closeAllFontMenus (leak-safe).
      const io =
        "IntersectionObserver" in window
          ? new IntersectionObserver(
              (entries, obs) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    if (entry.target.__font) loadFont(entry.target.__font);
                    obs.unobserve(entry.target);
                  }
                });
              },
              { root: fontMenu, rootMargin: "120px" },
            )
          : null;
      fontMenu.__fontObs = io;

      const rows = [];
      DATA.fonts.forEach((f) => {
        const opt = el(
          "button",
          {
            class: "nkd__font-opt",
            type: "button",
            "aria-selected": String(item.font && item.font.family === f.family),
            onclick: () => {
              item.font = f;
              State.font = f; // seeds the font of the next new text box
              loadFont(f); // make sure the picked face is fetched
              setFontLabel();
              closeAllFontMenus(); // hides + disconnects this menu's observer
              paintPreview();
            },
          },
          [
            el("span", {
              class: "nkd__font-opt-sample",
              text: item.text || f.family,
              style: {
                fontFamily: '"' + f.family + '", serif',
                fontStyle: f.style || "normal",
              },
            }),
            el("span", { class: "nkd__font-opt-grp", text: f.group || "" }),
          ],
        );
        opt.__font = f;
        fontMenu.appendChild(opt);
        rows.push(opt);
        if (io) io.observe(opt);
      });
      // no-IntersectionObserver fallback: load the first dozen so previews show
      if (!io) rows.slice(0, 12).forEach((o) => loadFont(o.__font));

      filter.addEventListener("input", () => {
        const q = filter.value.trim().toLowerCase();
        let loaded = 0;
        rows.forEach((o) => {
          const hit = !q || o.__font.family.toLowerCase().indexOf(q) !== -1;
          o.hidden = !hit;
          // eagerly load the first matches — a pure layout shift may not
          // re-trigger the observer for newly-exposed rows
          if (hit && loaded < 12) {
            loadFont(o.__font);
            loaded++;
          }
        });
      });
    };
    fontTrigger.addEventListener("click", () => {
      const willOpen = fontMenu.hidden;
      closeAllFontMenus();
      if (willOpen) {
        buildFontMenu();
        fontMenu.hidden = false;
      }
    });
    setFontLabel();
    row.appendChild(el("div", { class: "nkd__text-row-head" }, [input, del]));
    row.appendChild(
      el("div", { class: "nkd__font-row" }, [fontSelect, charCount]),
    );
    return row;
  }
  function addTextItem() {
    if (State.texts.length >= MAX_TEXTS) return;
    State.texts.push(makeTextItem(State.texts.length));
    refreshCurrentStepBody(); // rebuilds Step 3 from State.texts → new row appears
    const rows = $$(".nkd__text-row");
    const last = rows[rows.length - 1];
    const input = last && $(".nkd__input", last);
    if (input) input.focus();
  }
  function removeText(id) {
    const idx = State.texts.findIndex((t) => t.id === id);
    if (idx === -1) return;
    State.texts.splice(idx, 1);
    if (State.selectedLayer === "text:" + id) State.selectedLayer = null;
    paintPreview(); // re-renders the preview layers
    refreshSteps(); // rebuilds Step 3 body (row gone) + step-complete flags
  }

  function renderStepCustomize(body) {
    /* --- text boxes (one row per box, up to MAX_TEXTS) --- */
    const subText = el("div", { class: "nkd__sub" }, [
      el("div", { class: "nkd__sub-label", text: "Add text" }),
    ]);
    const rowsWrap = el("div", { class: "nkd__text-rows" });
    State.texts.forEach((item) => rowsWrap.appendChild(makeTextRow(item)));
    subText.appendChild(rowsWrap);
    const addBtn = el("button", {
      class: "nkd__add-text-btn",
      type: "button",
      text: "+ Add 2nd line",
      onclick: () => addTextItem(),
    });
    addBtn.disabled = State.texts.length >= MAX_TEXTS;
    subText.appendChild(addBtn);
    body.appendChild(subText);

    /* --- artwork upload --- */
    const subUpload = el("div", { class: "nkd__sub" }, [
      el("div", { class: "nkd__sub-label", text: "Upload artwork" }),
    ]);
    const uploadSlot = el("div", { "data-upload-slot": "" });
    const fileInput = el("input", {
      type: "file",
      hidden: true,
      accept: ".png,.jpg,.jpeg,.svg,.pdf,.ai,.heic,.heif",
      onchange: (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) handleUpload(f, uploadSlot, fileInput);
        e.target.value = "";
      },
    });
    subUpload.appendChild(uploadSlot);
    subUpload.appendChild(fileInput);
    renderUploadSlot(uploadSlot, fileInput);
    body.appendChild(subUpload);

    /* --- design colour --- */
    const subColour = el("div", { class: "nkd__sub" }, [
      el("div", { class: "nkd__sub-label", text: "Design color" }),
    ]);
    if (State.foil) {
      subColour.appendChild(
        el("div", { class: "nkd__note" }, [
          "Foil finish selected. ",
          el("button", {
            type: "button",
            text: "Switch to ink color",
            style: {
              textDecoration: "underline",
              color: "var(--nkd-chocolate)",
              fontWeight: 600,
            },
            onclick: () => {
              State.foil = false;
              refreshCurrentStepBody();
              paintPreview();
            },
          }),
          " to choose a flat ink.",
        ]),
      );
    }
    const colourZone = el("div", {
      class: "nkd__colourzone",
      "data-disabled": State.foil ? "" : null,
    });
    const tabsRow = el("div", { class: "nkd__tabs" });
    [
      { id: "default", label: "Default" },
      { id: "pantone", label: "Pantone" },
      { id: "custom", label: "Custom" },
    ].forEach((t) => {
      tabsRow.appendChild(
        el("button", {
          class: "nkd__tab",
          type: "button",
          text: t.label,
          "aria-selected": String(State.designColorTab === t.id),
          onclick: () => {
            State.designColorTab = t.id;
            refreshCurrentStepBody();
            paintPreview();
          },
        }),
      );
    });
    colourZone.appendChild(tabsRow);
    if (State.designColorTab === "default") {
      const grid = el("div", { class: "nkd__swatches nkd__swatches--4" });
      DATA.designColors.forEach((c) => {
        const sel = State.designColor && State.designColor.id === c.id;
        grid.appendChild(
          el("button", {
            class: "nkd__swatch",
            type: "button",
            title: c.name,
            "aria-pressed": String(!!sel),
            style: { background: c.hex },
            onclick: () => {
              State.designColor = c;
              // Foil mutex (both directions): selecting a flat ink swatch turns foil OFF.
              // Pantone + Custom tabs intentionally do NOT auto-disable foil — their inputs
              // refresh the step body on every keystroke / picker-change, which would steal
              // focus mid-typing. Foil-off path for those tabs is the inline "Switch to ink
              // color" link rendered above the colour zone.
              if (State.foil) State.foil = false;
              refreshCurrentStepBody();
              paintPreview();
            },
          }),
        );
      });
      colourZone.appendChild(grid);
    } else if (State.designColorTab === "pantone") {
      colourZone.appendChild(
        el("input", {
          class: "nkd__input",
          type: "text",
          value: State.pantone,
          placeholder: "Enter Pantone code (e.g. 186 C)",
          oninput: (e) => {
            State.pantone = e.target.value;
          },
        }),
      );
    } else {
      const swatch = el(
        "label",
        { class: "nkd__custom-swatch", style: { background: State.customHex } },
        [
          el("input", {
            type: "color",
            value: /^#[0-9a-fA-F]{6}$/.test(State.customHex)
              ? State.customHex
              : "#1c150f",
            oninput: (e) => {
              State.customHex = e.target.value;
              swatch.style.background = e.target.value;
              hexInput.value = e.target.value;
              paintPreview();
            },
          }),
        ],
      );
      const hexInput = el("input", {
        class: "nkd__input nkd__custom-hex",
        type: "text",
        value: State.customHex,
        oninput: (e) => {
          let v = e.target.value.trim();
          if (v && v[0] !== "#") v = "#" + v;
          State.customHex = v;
          if (/^#[0-9a-fA-F]{6}$/.test(v)) {
            swatch.style.background = v;
            paintPreview();
          }
        },
      });
      colourZone.appendChild(
        el("div", { class: "nkd__custom-picker" }, [swatch, hexInput]),
      );
    }
    subColour.appendChild(colourZone);
    body.appendChild(subColour);

    /* --- foil toggle --- */
    body.appendChild(
      toggleRow({
        on: State.foil,
        label: "Foil finish",
        meta:
          "mutually exclusive with ink color" +
          (FOIL_SURCHARGE() > 0
            ? " · +" + moneyUSD(FOIL_SURCHARGE()) + " / napkin"
            : " · no extra charge"),
        onChange: (v) => {
          State.foil = v;
          if (!State.foilColor) State.foilColor = DATA.foilColors[0];
          refreshCurrentStepBody();
          paintPreview();
        },
      }),
    );
    if (State.foil) {
      const fg = el("div", { class: "nkd__foil-grid" });
      DATA.foilColors.forEach((c) => {
        const sel = State.foilColor && State.foilColor.id === c.id;
        fg.appendChild(
          el(
            "button",
            {
              class: "nkd__foil-swatch nkd__foil--" + c.id,
              type: "button",
              "aria-pressed": String(!!sel),
              title: c.name,
              onclick: () => {
                State.foilColor = c;
                refreshCurrentStepBody();
                paintPreview();
              },
            },
            el("span", { text: c.name }),
          ),
        );
      });
      body.appendChild(fg);
    }

    /* --- help link --- */
    body.appendChild(
      el("div", { class: "nkd__help-link" }, [
        "Prefer we design it for you? ",
        el("a", { href: CONTACT_URL, text: "Contact us" }),
        ".",
      ]),
    );
  }

  // A branded CSS spinner (chocolate accent ring). `sm` = the smaller modal variant.
  function nkdSpinner(sm) {
    return el("div", {
      class: "nkd__spinner" + (sm ? " nkd__spinner--sm" : ""),
      role: "status",
      "aria-label": "Loading",
    });
  }
  // Inline warning glyph for the error panel (inherits currentColor).
  const NKD_WARN_SVG =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M12 3.2 1.8 21h20.4L12 3.2Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
    '<path d="M12 9.5v4.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<circle cx="12" cy="17" r="1.05" fill="currentColor"/></svg>';

  // Upload "processing" state — replaces the dropzone while the file decodes +
  // vectorises server-side. Keeps the customer informed during the round-trip.
  function renderUploadProcessing(slot, label, sub) {
    slot.innerHTML = "";
    slot.appendChild(
      el("div", { class: "nkd__uploading" }, [
        nkdSpinner(),
        el("div", { class: "nkd__uploading-label", text: label }),
        sub ? el("div", { class: "nkd__uploading-sub", text: sub }) : null,
      ]),
    );
  }
  // Upload error — re-render a fresh dropzone (so the customer can retry), then
  // show a branded error panel beneath it. Also toasts.
  function renderUploadError(slot, fileInput, msg) {
    renderUploadSlot(slot, fileInput);
    slot.appendChild(
      el("div", { class: "nkd__upload-error", role: "alert" }, [
        el("span", { class: "nkd__upload-error-ic", html: NKD_WARN_SVG }),
        el("span", { class: "nkd__upload-error-msg", text: msg }),
      ]),
    );
    toast(msg, "error");
  }

  function renderUploadSlot(slot, fileInput) {
    slot.innerHTML = "";
    if (State.logo) {
      slot.appendChild(
        el("div", { class: "nkd__file-chip" }, [
          el(
            "div",
            { class: "nkd__file-thumb" },
            el("img", { src: State.logo.src, alt: "" }),
          ),
          el("div", { class: "nkd__file-name", text: State.logo.name }),
          el("button", {
            class: "nkd__file-remove",
            type: "button",
            text: "Remove",
            onclick: () => {
              State.logo = null;
              State.logoBox = null;
              if (State.selectedLayer === "logo") State.selectedLayer = null;
              renderUploadSlot(slot, fileInput);
              paintPreview();
              refreshSteps(); // step-3 complete flag may change
            },
          }),
        ]),
      );
      if (State.logo.opaque) {
        slot.appendChild(
          el("div", {
            class: "nkd__help-link",
            style: { marginTop: "8px" },
            text: "Full-colour artwork detected — our team will confirm the print colours with you. For single-ink printing, upload art on a transparent background.",
          }),
        );
      }
    } else {
      const dz = el(
        "div",
        {
          class: "nkd__dropzone",
          onclick: () => fileInput.click(),
          ondragover: (e) => {
            e.preventDefault();
            dz.setAttribute("data-drag", "");
          },
          ondragleave: () => dz.removeAttribute("data-drag"),
          ondrop: (e) => {
            e.preventDefault();
            dz.removeAttribute("data-drag");
            const f =
              e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
            if (f) handleUpload(f, slot, fileInput);
          },
        },
        [
          el("div", {
            class: "nkd__dropzone-hd",
            text: "Drag & drop your file",
          }),
          el("div", {
            class: "nkd__dropzone-sub",
            text: "PNG, JPG, SVG, PDF, AI, HEIC",
          }),
          el("span", { class: "nkd__dropzone-btn", text: "Upload" }),
        ],
      );
      slot.appendChild(dz);
      // Errors are rendered by renderUploadError() as a branded panel appended here.
    }
  }

  /* ---------------------------------------------------- upload pipeline ------ */
  /* Snap the logo box to the artwork's natural aspect ratio: contain-fit inside
     the current box, centered. Every render path (editor mask, canvas export,
     server proof) letterboxes the artwork inside its box anyway, so this never
     changes what the customer sees, it just makes the selection handles hug
     the artwork so the aspect-locked resize feels right. */
  function snapLogoBoxToArtwork() {
    if (!State.logo || !State.logoBox) return;
    const src = State.logo.src;
    const img = new Image();
    img.onload = () => {
      if (!State.logo || State.logo.src !== src || !State.logoBox) return;
      const ar = img.naturalWidth / Math.max(1, img.naturalHeight);
      if (!isFinite(ar) || ar <= 0) return;
      const b = State.logoBox;
      let w = b.w,
        h = b.h;
      if (w / h > ar) w = h * ar;
      else h = w / ar;
      b.x += (b.w - w) / 2;
      b.y += (b.h - h) / 2;
      b.w = w;
      b.h = h;
      paintPreview();
    };
    img.src = src;
  }

  function handleUpload(file, slot, fileInput) {
    // Branded error panel + toast; re-renders a fresh dropzone so the customer
    // can retry. (Replaces the old dim-the-dropzone + tiny-red-text states.)
    const fail = (msg) => renderUploadError(slot, fileInput, msg);
    const name = file.name || "artwork";
    const lower = name.toLowerCase();
    const ext = (lower.match(/\.([a-z0-9]+)$/) || [, ""])[1];

    const finishWithDataURL = (dataUrl) => {
      // Diamond compensation: see the equivalent block in the text input's
      // oninput. Storing -napkinRotation() in box.rotation makes the layer
      // appear visually horizontal in the editor on diamond products.
      const initialBox = {
        ...DEFAULT_LOGO_BOX,
        rotation: -napkinRotation(),
      };
      // Vector Print Pipeline (Gate 1): validate + vectorise the upload server-side.
      // A photo is BLOCKED here with guidance; a logo shows its vectorised result.
      if (CFG.vectorPipeline) {
        const vext = /^data:image\/svg/i.test(dataUrl)
          ? "svg"
          : /^data:image\/jpe?g/i.test(dataUrl)
            ? "jpg"
            : "png";
        vppVectorize(dataUrl, vext)
          .then((res) => {
            if (res && res.ok) {
              State.logo = {
                src: res.previewDataUrl || dataUrl,
                name: name,
                type: vext,
                // The vectorised preview is a single-colour silhouette on a
                // transparent ground — treat it as an alpha MASK so it recolours
                // to the selected design (ink) colour, exactly like text.
                // (opaque:true would draw it as-is and freeze it black.)
                opaque: false,
                vector: res.vector,
                originalUrl: res.originalUrl,
              };
              State.logoBox = { ...initialBox };
              snapLogoBoxToArtwork();
              State.touched[3] = true; // artwork accepted
              renderUploadSlot(slot, fileInput);
              paintPreview();
              refreshSteps();
            } else if (res && res.ok === false) {
              fail(
                (res.reasons && res.reasons[0]) ||
                  "We can't print that image — please upload a logo or line-art on a transparent background.",
              );
            } else {
              fail(
                "We couldn't process your artwork just now — please try again.",
              );
            }
          })
          .catch(() =>
            fail(
              "We couldn't process your artwork just now — please try again.",
            ),
          );
        return;
      }
      // detect transparency → decide mask-recolor vs render-as-is
      detectOpacity(dataUrl)
        .then((opaque) => {
          State.logo = { src: dataUrl, name, type: ext, opaque };
          State.logoBox = { ...initialBox };
          snapLogoBoxToArtwork();
          State.touched[3] = true; // artwork accepted
          renderUploadSlot(slot, fileInput);
          paintPreview();
          refreshSteps();
        })
        .catch(() => {
          State.logo = { src: dataUrl, name, type: ext, opaque: false };
          State.logoBox = { ...initialBox };
          snapLogoBoxToArtwork();
          State.touched[3] = true; // artwork accepted
          renderUploadSlot(slot, fileInput);
          paintPreview();
          refreshSteps();
        });
    };

    if (file.size > 25 * 1024 * 1024)
      return fail("That file is over 25 MB — please upload a smaller version.");

    // Show the branded processing card for the whole decode + (flag-ON) vectorise
    // round-trip, so the customer knows their file is being worked on.
    renderUploadProcessing(
      slot,
      "Processing your artwork…",
      CFG.vectorPipeline ? "Checking it's print-ready" : null,
    );

    if (ext === "heic" || ext === "heif" || /heic|heif/.test(file.type)) {
      if (typeof heic2any === "undefined")
        return fail(
          "HEIC support is still loading — please try again in a moment.",
        );
      heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 })
        .then((out) => readBlobAsDataURL(Array.isArray(out) ? out[0] : out))
        .then(finishWithDataURL)
        .catch(() =>
          fail(
            "Couldn't read that HEIC file — try exporting it as a PNG or JPG.",
          ),
        );
      return;
    }
    if (ext === "pdf" || ext === "ai" || file.type === "application/pdf") {
      if (typeof pdfjsLib === "undefined")
        return fail(
          "PDF support is still loading — please try again in a moment.",
        );
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc)
        // Self-hosted worker (theme asset) so vector uploads survive any CDN-blocked
        // network (e.g. mainland China). CDN URL kept as the last-ditch fallback only.
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          CFG.pdfWorkerUrl ||
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      file
        .arrayBuffer()
        .then((buf) => pdfjsLib.getDocument({ data: buf }).promise)
        .then((pdf) => pdf.getPage(1))
        .then((page) => {
          const vp1 = page.getViewport({ scale: 1 });
          const scale = Math.min(
            4,
            Math.max(1, 1400 / Math.max(vp1.width, vp1.height)),
          );
          const vp = page.getViewport({ scale });
          const c = el("canvas");
          c.width = Math.ceil(vp.width);
          c.height = Math.ceil(vp.height);
          return page
            .render({ canvasContext: c.getContext("2d"), viewport: vp })
            .promise.then(() => c.toDataURL("image/png"));
        })
        .then(finishWithDataURL)
        .catch(() =>
          fail(
            "Couldn't render that " +
              (ext || "PDF").toUpperCase() +
              " — try exporting page 1 as a PNG.",
          ),
        );
      return;
    }
    if (ext === "svg" || file.type === "image/svg+xml") {
      readBlobAsDataURL(file)
        .then(finishWithDataURL)
        .catch(() => fail("Couldn't read that SVG."));
      return;
    }
    // raster (png/jpg/gif/webp)
    if (
      /^image\//.test(file.type) ||
      ["png", "jpg", "jpeg", "gif", "webp"].indexOf(ext) !== -1
    ) {
      readBlobAsDataURL(file)
        .then(finishWithDataURL)
        .catch(() => fail("Couldn't read that image."));
      return;
    }
    fail(
      "Unsupported file type. Please upload a PNG, JPG, SVG, PDF, AI or HEIC.",
    );
  }
  function readBlobAsDataURL(blob) {
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  }
  function detectOpacity(dataUrl) {
    // returns true if the image has (essentially) no transparency
    return new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => {
        try {
          const W = 40,
            H = Math.max(
              1,
              Math.round(
                (40 * img.naturalHeight) / Math.max(1, img.naturalWidth),
              ),
            );
          const c = el("canvas");
          c.width = W;
          c.height = H;
          const ctx = c.getContext("2d");
          ctx.drawImage(img, 0, 0, W, H);
          const d = ctx.getImageData(0, 0, W, H).data;
          let transparentPixels = 0;
          for (let i = 3; i < d.length; i += 4)
            if (d[i] < 250) transparentPixels++;
          res(transparentPixels / (W * H) < 0.02);
        } catch (e) {
          rej(e);
        }
      };
      img.onerror = rej;
      img.src = dataUrl;
    });
  }

  /* ------------------------------------------------------------ Step 4 — Order */
  function renderStepOrder(body) {
    const key = State.product.key;
    const tiers = tiersFor(key);
    const country = (CFG.market && CFG.market.country) || "US";
    const coef = marketCoefficient(country);
    const isQuote = State.qty >= QUOTE_AT();
    const perNapkin = unitPriceUSD(key, State.qty) * coef;
    const foilTotal = State.foil ? State.qty * FOIL_SURCHARGE() * coef : 0;
    const sampleTotal =
      State.sample && State.qty >= SAMPLE_MIN() ? SAMPLE_PRICE() : 0;
    const total = State.qty * perNapkin + foilTotal + sampleTotal;
    const actIdx = activeTierIndex(key, State.qty);

    /* volume bar */
    const bar = el("div", { class: "nkd__tiers" });
    tiers.forEach((t, i) => {
      bar.appendChild(
        el(
          "div",
          { class: "nkd__tier", "data-active": i === actIdx ? "" : null },
          [
            el("div", {
              class: "nkd__tier-price",
              text: moneyUSD(t.unit_price * coef),
            }),
            el("div", {
              class: "nkd__tier-min",
              text: t.min >= 1000 ? t.min / 1000 + "k+" : t.min + "+",
            }),
          ],
        ),
      );
    });
    body.appendChild(
      el("div", { class: "nkd__sub" }, [
        el("div", { class: "nkd__sub-label", text: "Volume pricing" }),
        bar,
      ]),
    );

    /* qty stepper */
    const dec = el("button", {
      class: "nkd__qty-btn",
      type: "button",
      text: "−",
      disabled: State.qty <= MOQ(),
      onclick: () => setQty(State.qty - QTY_STEP()),
    });
    const inc = el("button", {
      class: "nkd__qty-btn",
      type: "button",
      text: "+",
      onclick: () => setQty(State.qty + QTY_STEP()),
    });
    const qtyInput = el("input", {
      class: "nkd__input nkd__qty-input",
      type: "number",
      min: String(MOQ()),
      step: String(QTY_STEP()),
      value: String(State.qty),
      onchange: (e) => setQty(parseInt(e.target.value, 10) || MOQ()),
    });
    body.appendChild(
      el("div", { class: "nkd__qty-row" }, [dec, qtyInput, inc]),
    );

    /* price readout */
    if (!isQuote) {
      body.appendChild(
        el("div", { class: "nkd__price-line" }, [
          el("span", { class: "nkd__price-big", text: moneyUSD(perNapkin) }),
          el("span", { class: "nkd__price-unit", text: "/ napkin" }),
          el("span", { class: "nkd__price-sep", text: "·" }),
          el("span", { class: "nkd__price-big", text: moneyUSD(total) }),
          el("span", { class: "nkd__price-unit", text: "total" }),
        ]),
      );
      if (coef !== 1)
        body.appendChild(
          el("div", {
            class: "nkd__nudge",
            text: "Prices shown in USD — converted to your local currency at checkout.",
          }),
        );
      const nudge = makeNudge(key, State.qty);
      if (nudge)
        body.appendChild(el("div", { class: "nkd__nudge", text: nudge }));
    } else {
      body.appendChild(
        el("div", {
          class: "nkd__quote-note",
          text:
            "Orders of " +
            QUOTE_AT().toLocaleString() +
            "+ napkins are quoted individually — including freight, foil and international handling.",
        }),
      );
    }

    /* sample */
    if (State.qty >= SAMPLE_MIN() && !isQuote) {
      body.appendChild(
        el("div", { class: "nkd__sample-block" }, [
          toggleRow({
            on: State.sample,
            inline: true,
            label: "Add a physical sample",
            meta: moneyUSD(SAMPLE_PRICE()) + " · ships before the full run",
            onChange: (v) => {
              State.sample = v;
              State.touched[4] = true;
              refreshCurrentStepBody();
            },
          }),
        ]),
      );
    }

    /* CTA */
    if (isQuote) {
      body.appendChild(
        el("a", {
          class: "nkd__btn nkd__btn--primary nkd__btn--full nkd__order-cta",
          href: CONTACT_URL,
          text: "Request a custom quote",
        }),
      );
    } else {
      body.appendChild(
        el("button", {
          class: "nkd__btn nkd__btn--primary nkd__btn--full nkd__order-cta",
          type: "button",
          text: "Preview napkin",
          onclick: () => {
            State.touched[4] = true; // reviewing the order counts as doing Step 4
            openModal();
          },
        }),
      );
    }
  }
  function setQty(v) {
    let q = Math.max(
      MOQ(),
      Math.round((parseInt(v, 10) || MOQ()) / QTY_STEP()) * QTY_STEP(),
    );
    if (q < MOQ()) q = MOQ();
    State.qty = q;
    State.touched[4] = true;
    if (State.qty < SAMPLE_MIN() && State.sample) State.sample = false;
    refreshSteps();
  }

  /* ------------------------------------------------------------- toggle row */
  function toggleRow({ on, label, meta, onChange, inline }) {
    const sw = el("button", {
      class: "nkd__switch",
      type: "button",
      role: "switch",
      "aria-checked": String(!!on),
      onclick: () => {
        const next = sw.getAttribute("aria-checked") !== "true";
        sw.setAttribute("aria-checked", String(next));
        onChange(next);
      },
    });
    return el(
      "div",
      { class: "nkd__toggle-row" + (inline ? " nkd__toggle-row--inline" : "") },
      [
        el("div", {}, [
          el("div", { class: "nkd__toggle-label", text: label }),
          meta ? el("div", { class: "nkd__toggle-meta", text: meta }) : null,
        ]),
        sw,
      ],
    );
  }

  /* ============================================================ layer editor */
  /* Diamond products are presented tilted 45° in the editor, matching the on-table
     appearance the customer is buying. The napkin (+ its imprint + safe-zone children)
     all rotate together via a single CSS transform on dom.napkin. The NSEW grid lives at
     wrap-level (sibling of napkin) and stays screen-axis-aligned so the customer always
     has a fixed reference frame. Layer rotation `box.rotation` is stored in IMPRINT frame:
       - Square:  default 0  → screen angle 0  (napkin not rotated)
       - Diamond: default -45 → screen angle 0  (compounded with napkin's +45° parent)
     Storage in imprint frame keeps the export render path (renderToCanvas) untouched. */
  function napkinRotation() {
    return State.product && State.product.shape === "diamond" ? 45 : 0;
  }
  function ensureBoxes() {
    const napkinRot = napkinRotation();
    // Defensive — every text item is created with its own box (makeTextItem).
    State.texts.forEach((t) => {
      if (!t.box) t.box = { ...DEFAULT_TEXT_BOX, rotation: -napkinRot };
    });
    if (State.logo && !State.logoBox)
      State.logoBox = { ...DEFAULT_LOGO_BOX, rotation: -napkinRot };
  }
  function textItemById(id) {
    for (let i = 0; i < State.texts.length; i++)
      if (State.texts[i].id === id) return State.texts[i];
    return null;
  }
  // Resolve a compound layer id ("logo" | "text:<id>") → { kind, box, item }.
  // Returns null for a stale id (e.g. a deleted text box); callers null-guard.
  function resolveLayer(layerId) {
    if (layerId === "logo")
      return { kind: "logo", box: State.logoBox, item: null };
    if (typeof layerId === "string" && layerId.indexOf("text:") === 0) {
      const item = textItemById(+layerId.slice(5));
      return item ? { kind: "text", box: item.box, item: item } : null;
    }
    return null;
  }
  function currentImprintPx() {
    const L = NKD.geo.layout(State.product, currentNapkinDisplaySize());
    return L.imprintPx;
  }
  // Build (or rebuild) the layer DOM inside the imprint.
  function renderLayers() {
    ensureBoxes();
    const imp = dom.imprint;
    // wipe layer els only — by class, so it can't touch anything else. (The safe-zone and
    // grid SVGs live outside the imprint and are managed separately by paintPreview.)
    $$(".nkd__layer", imp).forEach((n) => n.remove());
    dom.layerEls = {};
    // Layer order: logo under text (mockup order), then each text box that has
    // content. The selected layer is appended LAST so its handles + delete
    // control stay on top when boxes overlap.
    const ids = [];
    if (State.logo && State.logoBox) ids.push("logo");
    State.texts.forEach((t) => {
      if (t.text) ids.push("text:" + t.id);
    });
    const ordered = ids.filter((id) => id !== State.selectedLayer);
    if (ids.indexOf(State.selectedLayer) !== -1)
      ordered.push(State.selectedLayer);
    ordered.forEach((id) => {
      const node = makeLayerEl(id);
      dom.layerEls[id] = node;
      imp.appendChild(node);
    });
    positionLayers();
    // angle pill
    if (dom.anglePill) dom.anglePill.remove();
    dom.anglePill = null;
    if (State.rotatingAngle != null) {
      dom.anglePill = el("div", { class: "nkd__angle-pill" });
      updateAnglePill();
      dom.previewWrap.appendChild(dom.anglePill);
    }
  }
  function updateAnglePill() {
    if (!dom.anglePill) return;
    const a = Math.round(State.rotatingAngle || 0);
    dom.anglePill.innerHTML = a + "°" + (a === 0 ? "<em>· locked</em>" : "");
  }
  function makeLayerEl(layerId) {
    const L = resolveLayer(layerId);
    if (!L) return el("div"); // defensive — stale layer id
    const kind = L.kind;
    const selected = State.selectedLayer === layerId;
    const node = el("div", {
      class: "nkd__layer",
      "data-layer": layerId,
      "data-selected": selected ? "" : null,
    });
    node.appendChild(
      el("div", {
        class: kind === "text" ? "nkd__layer-text" : "nkd__layer-img",
      }),
    );
    if (selected) {
      ["nw", "n", "ne", "e", "se", "s", "sw", "w"].forEach((h) => {
        const handle = el("div", { class: "nkd__handle nkd__handle--" + h });
        handle.addEventListener("pointerdown", (e) =>
          beginDrag(e, layerId, "resize-" + h),
        );
        node.appendChild(handle);
      });
      node.appendChild(el("div", { class: "nkd__rotate-stem" }));
      const knob = el("div", { class: "nkd__rotate-knob", html: ICONS.rotate });
      knob.addEventListener("pointerdown", (e) =>
        beginDrag(e, layerId, "rotate"),
      );
      node.appendChild(knob);
      // Text layers carry a delete (×) control. The logo is removed via its
      // own upload slot, so no × there.
      if (kind === "text" && L.item) {
        const del = el("button", {
          class: "nkd__layer-del",
          type: "button",
          "aria-label": "Delete this text box",
          text: "×",
        });
        // stop the pointerdown bubbling to the layer (would start a move-drag)
        // and to the document deselect handler.
        del.addEventListener("pointerdown", (e) => e.stopPropagation());
        del.addEventListener("click", (e) => {
          e.stopPropagation();
          removeText(L.item.id);
        });
        node.appendChild(del);
      }
    }
    node.addEventListener("pointerdown", (e) => {
      // ignore if it started on a handle/knob/delete (those have their own listeners)
      if (e.target.closest(".nkd__handle, .nkd__rotate-knob, .nkd__layer-del"))
        return;
      if (State.selectedLayer !== layerId) {
        State.selectedLayer = layerId;
        renderLayers();
        // after re-render, start a move-drag on the new node
        const fresh = dom.layerEls[layerId];
        if (fresh) beginDragFrom(e, layerId, "move", fresh);
        return;
      }
      beginDrag(e, layerId, "move");
    });
    return node;
  }
  function styleTextContent(node, item) {
    const t = $(".nkd__layer-text", node);
    if (!t || !item) return;
    // The text lives in a content-sized inner span. The foil gradient must be
    // painted on THAT span: background-clip:text only renders glyphs that sit
    // inside the painted background area, so painting the fixed-size flex box
    // instead truncates any text that overflows the box (the foil "bottom half
    // cut off" bug).
    let inner = t.firstElementChild;
    if (!inner || !inner.classList.contains("nkd__layer-text-in")) {
      t.textContent = "";
      inner = el("span", { class: "nkd__layer-text-in" });
      t.appendChild(inner);
    }
    const f = item.font || DATA.fonts[0] || { family: "Work Sans" };
    inner.textContent = item.text;
    t.style.fontFamily = '"' + f.family + '", serif';
    t.style.fontStyle = f.style || "normal";
    const impPx = currentImprintPx();
    t.style.fontSize =
      Math.max(8, ((item.box && item.box.fontFrac) || 0.12) * impPx) + "px";
    if (State.foil) {
      inner.style.color = "transparent";
      inner.style.background = foilGradientCSS(
        State.foilColor && State.foilColor.id,
      );
      inner.style.webkitBackgroundClip = "text";
      inner.style.backgroundClip = "text";
    } else {
      inner.style.color = previewInkHex();
      inner.style.background = "none";
      inner.style.webkitBackgroundClip = "border-box";
      inner.style.backgroundClip = "border-box";
    }
  }
  function styleImgContent(node) {
    const img = $(".nkd__layer-img", node);
    if (!img || !State.logo) return;
    const url = State.logo.src;
    if (State.logo.opaque && !State.foil) {
      // render full-colour as-is
      img.style.webkitMaskImage = "none";
      img.style.maskImage = "none";
      img.style.background =
        "center / contain no-repeat url('" + url.replace(/'/g, "%27") + "')";
    } else {
      // single-ink: use the artwork's alpha as a mask, fill with ink (or foil gradient)
      img.style.webkitMaskImage = "url('" + url.replace(/'/g, "%27") + "')";
      img.style.maskImage = "url('" + url.replace(/'/g, "%27") + "')";
      img.style.background = State.foil
        ? foilGradientCSS(State.foilColor && State.foilColor.id)
        : previewInkHex();
    }
  }
  function positionLayers() {
    const impPx = currentImprintPx();
    Object.keys(dom.layerEls).forEach((layerId) => {
      const node = dom.layerEls[layerId];
      const L = resolveLayer(layerId);
      if (!node || !L || !L.box) return;
      const box = L.box;
      node.style.left = box.x * impPx + "px";
      node.style.top = box.y * impPx + "px";
      node.style.width = box.w * impPx + "px";
      node.style.height = box.h * impPx + "px";
      node.style.transform = "rotate(" + (box.rotation || 0) + "deg)";
      if (L.kind === "text") styleTextContent(node, L.item);
      else styleImgContent(node);
    });
  }

  /* drag state machine */
  let drag = null;
  function beginDrag(e, layerId, mode) {
    e.stopPropagation();
    e.preventDefault();
    if (State.selectedLayer !== layerId) {
      State.selectedLayer = layerId;
      renderLayers();
    }
    beginDragFrom(e, layerId, mode, dom.layerEls[layerId]);
  }
  function beginDragFrom(e, layerId, mode, node) {
    if (!node) return;
    const L = resolveLayer(layerId);
    if (!L || !L.box) return;
    const impPx = currentImprintPx();
    drag = {
      layerId,
      kind: L.kind,
      mode,
      node,
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...L.box },
      impPx,
    };
    try {
      node.setPointerCapture && node.setPointerCapture(e.pointerId);
    } catch (_) {}
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragUp);
    window.addEventListener("pointercancel", onDragUp);
  }
  function onDragMove(e) {
    if (!drag) return;
    const L = resolveLayer(drag.layerId);
    if (!L || !L.box) return onDragUp(); // box deleted mid-drag — bail cleanly
    const box = L.box;
    const o = drag.orig;
    const impPx = drag.impPx || 1;
    // Screen pixel deltas. Move is in imprint-frame (box.x/y are imprint-frame coords),
    // so we inverse-rotate by napkinRotation: on diamond the napkin's CSS +45° rotates
    // the imprint, and an undone +45° puts screen-east drag back onto imprint-east.
    // Resize is intentionally in screen-frame (no inverse-rotation), matching the demo's
    // "drag the handle in whatever direction you see it" model — for diamond-default
    // layers the compounded rotation cancels (-45 layer + 45 napkin = 0), so screen-east
    // drag of the east handle widens the layer screen-east as expected. For customer-
    // rotated layers the resize follows the layer's local axis (demo limitation, accepted).
    // The rotate branch uses getBoundingClientRect which is already in screen coords.
    const dxScreen = e.clientX - drag.startX;
    const dyScreen = e.clientY - drag.startY;
    const napkinRot = napkinRotation();
    const θ = (napkinRot * Math.PI) / 180;
    const cosθ = Math.cos(θ);
    const sinθ = Math.sin(θ);
    const dxImpF = (dxScreen * cosθ + dyScreen * sinθ) / impPx;
    const dyImpF = (-dxScreen * sinθ + dyScreen * cosθ) / impPx;
    const dxScreenF = dxScreen / impPx;
    const dyScreenF = dyScreen / impPx;
    if (drag.mode === "move") {
      // Box position clamp: allow the box to extend up to HALF its width/height
      // past the imprint in any direction. The box CENTRE stays inside the
      // imprint [0, 1], which means:
      //   (a) the layer is never "lost" — at least half of it remains visible
      //       and grabbable inside the imprint
      //   (b) centered text reaches the imprint edges/corners (because the
      //       text is centered in the box and the box center can sit on the
      //       imprint edge)
      // The actual visual + print clipping is NOT done here — it's done by
      // the imprint's `overflow: hidden` (DOM) + `ctx.clip()` in
      // renderToCanvas (export). Ink past the imprint edge is never visible
      // and never printed, regardless of how far the box extends.
      // Known trade-off: the imprint's overflow:hidden also clips the resize
      // handles + rotate knob on the over-extended side. Pre-existing UX;
      // fixing it requires moving the handles out of the imprint hierarchy.
      // Deferred to a later round.
      let nx = o.x + dxImpF,
        ny = o.y + dyImpF;
      nx = clamp(nx, -o.w * 0.5, 1 - o.w * 0.5);
      ny = clamp(ny, -o.h * 0.5, 1 - o.h * 0.5);
      // Subtle snap-to-centre: when the box centre comes within ~7 screen px of
      // the imprint centre on an axis, lock that axis to dead centre and show a
      // momentary guide line. Escapable by design — the raw pointer position
      // keeps tracking, so dragging past the threshold releases the snap.
      const snapThr = 7 / impPx;
      let snapX = false,
        snapY = false;
      if (napkinRot === 0) {
        // Square: imprint frame == screen frame, so the imprint centre-lines ARE
        // the screen centre-lines. Existing behaviour — deliberately untouched.
        if (Math.abs(nx + o.w / 2 - 0.5) <= snapThr) {
          nx = 0.5 - o.w / 2;
          snapX = true;
        }
        if (Math.abs(ny + o.h / 2 - 0.5) <= snapThr) {
          ny = 0.5 - o.h / 2;
          snapY = true;
        }
      } else {
        // Diamond: the imprint is CSS-rotated +45°, so its centre-lines run
        // diagonally on screen and the snap would lock along the 45° axes. Snap on
        // the SCREEN axes instead so the lock (and the guide lines) read as true
        // vertical/horizontal to the customer. Take the box-centre offset from the
        // imprint centre, rotate imprint→screen (+napkinRot), snap each screen
        // axis, then rotate back to imprint frame for storage. (cosθ/sinθ are the
        // same napkin-rotation terms computed above for the move delta.)
        const ox = nx + o.w / 2 - 0.5,
          oy = ny + o.h / 2 - 0.5;
        let sx = ox * cosθ - oy * sinθ,
          sy = ox * sinθ + oy * cosθ;
        if (Math.abs(sx) <= snapThr) {
          sx = 0;
          snapX = true;
        }
        if (Math.abs(sy) <= snapThr) {
          sy = 0;
          snapY = true;
        }
        const ox2 = sx * cosθ + sy * sinθ,
          oy2 = -sx * sinθ + sy * cosθ;
        nx = ox2 + 0.5 - o.w / 2;
        ny = oy2 + 0.5 - o.h / 2;
      }
      showCentreGuides(snapX, snapY);
      box.x = nx;
      box.y = ny;
    } else if (drag.mode === "rotate") {
      const r = drag.node.getBoundingClientRect();
      const px = e.clientX - (r.left + r.width / 2);
      const py = e.clientY - (r.top + r.height / 2);
      // SCREEN-frame angle from layer centre to the pointer (what the customer sees).
      let screenAngle = (Math.atan2(py, px) * 180) / Math.PI + 90;
      if (screenAngle > 180) screenAngle -= 360;
      if (screenAngle < -180) screenAngle += 360;
      // Snap on the SCREEN angle so the customer snaps to visible vertical/horizontal.
      for (const target of [0, 90, -90, 180, -180]) {
        if (Math.abs(screenAngle - target) <= 2.5) {
          screenAngle = target === -180 ? 180 : target;
          break;
        }
      }
      // Store in IMPRINT frame so the export path (and the parent napkin's CSS rotation)
      // compose correctly: visible angle = stored rotation + napkin rotation.
      box.rotation = screenAngle - napkinRot;
      // The pill shows what the customer sees (screen angle), not the stored value.
      State.rotatingAngle = screenAngle;
      if (!dom.anglePill) {
        dom.anglePill = el("div", { class: "nkd__angle-pill" });
        dom.previewWrap.appendChild(dom.anglePill);
      }
      updateAnglePill();
    } else {
      // resize-XX — handles are attached to the layer's laid-out edges. Use SCREEN-frame
      // deltas (no inverse-rotation): for diamond-default layers the compounded rotation
      // cancels, so screen-east drag of the east handle widens screen-east correctly.
      // Customer-rotated layers fall back to "drag follows the layer's local axis" (the
      // demo's accepted limitation).
      const h = drag.mode.slice(7); // nw/n/ne/e/se/s/sw/w
      const minF = 30 / impPx;
      let x = o.x,
        y = o.y,
        w = o.w,
        hgt = o.h;
      // Uploaded artwork keeps its proportions: every handle scales the box
      // uniformly (corner = dominant axis, edge = its own axis). Shift restores
      // free-form. Text layers keep the existing free-form model (their content
      // reflows; artwork letterboxes, so a non-uniform box just breaks the
      // handle fit).
      const lockAspect = drag.kind === "logo" && !e.shiftKey;
      if (lockAspect) {
        let pw = o.w,
          ph = o.h;
        if (h.indexOf("e") !== -1) pw = o.w + dxScreenF;
        if (h.indexOf("w") !== -1) pw = o.w - dxScreenF;
        if (h.indexOf("s") !== -1) ph = o.h + dyScreenF;
        if (h.indexOf("n") !== -1) ph = o.h - dyScreenF;
        const swF = pw / Math.max(1e-4, o.w);
        const shF = ph / Math.max(1e-4, o.h);
        let s;
        if (h === "e" || h === "w") s = swF;
        else if (h === "n" || h === "s") s = shF;
        else s = Math.abs(swF - 1) >= Math.abs(shF - 1) ? swF : shF;
        // Anchors: the opposite edge/corner stays put; pure-edge handles keep
        // the cross axis centered. Room clamps keep the box inside the imprint.
        const anchorRight = h.indexOf("w") !== -1;
        const anchorBottom = h.indexOf("n") !== -1;
        const edgeEW = h === "e" || h === "w";
        const edgeNS = h === "n" || h === "s";
        const roomW = edgeNS
          ? 2 * Math.min(o.x + o.w / 2, 1 - (o.x + o.w / 2))
          : anchorRight
            ? o.x + o.w
            : 1 - o.x;
        const roomH = edgeEW
          ? 2 * Math.min(o.y + o.h / 2, 1 - (o.y + o.h / 2))
          : anchorBottom
            ? o.y + o.h
            : 1 - o.y;
        const sMin = minF / Math.max(1e-4, Math.min(o.w, o.h));
        const sMax = Math.min(
          roomW / Math.max(1e-4, o.w),
          roomH / Math.max(1e-4, o.h),
        );
        s = clamp(s, sMin, Math.max(sMin, sMax));
        w = o.w * s;
        hgt = o.h * s;
        x = edgeNS ? o.x + (o.w - w) / 2 : anchorRight ? o.x + o.w - w : o.x;
        y = edgeEW
          ? o.y + (o.h - hgt) / 2
          : anchorBottom
            ? o.y + o.h - hgt
            : o.y;
        box.x = x;
        box.y = y;
        box.w = w;
        box.h = hgt;
        positionLayers();
        return;
      }
      if (h.indexOf("e") !== -1) w = Math.max(minF, o.w + dxScreenF);
      if (h.indexOf("w") !== -1) {
        w = Math.max(minF, o.w - dxScreenF);
        x = o.x + (o.w - w);
      }
      if (h.indexOf("s") !== -1) hgt = Math.max(minF, o.h + dyScreenF);
      if (h.indexOf("n") !== -1) {
        hgt = Math.max(minF, o.h - dyScreenF);
        y = o.y + (o.h - hgt);
      }
      // Clamp into the imprint after resize so the layer can't grow past the
      // 4″ printable area. Order: snap x/y into [0, 1] first, then clip w/h to
      // what fits, then re-enforce the minF floor. Without this, the e-handle
      // would let w exceed 1 and the next move-drag would collapse to x = 0.
      if (x < 0) {
        w += x;
        x = 0;
      }
      if (y < 0) {
        hgt += y;
        y = 0;
      }
      if (x + w > 1) w = 1 - x;
      if (y + hgt > 1) hgt = 1 - y;
      w = Math.max(minF, w);
      hgt = Math.max(minF, hgt);
      box.x = x;
      box.y = y;
      box.w = w;
      box.h = hgt;
      const isCorner =
        (h.indexOf("e") !== -1 || h.indexOf("w") !== -1) &&
        (h.indexOf("n") !== -1 || h.indexOf("s") !== -1);
      if (drag.kind === "text" && isCorner) {
        const ratio = hgt / Math.max(1e-4, o.h);
        box.fontFrac = clamp((o.fontFrac || 0.12) * ratio, 0.02, 0.6);
      }
    }
    positionLayers();
  }
  /* Centre snap guides — momentary aqua lines through the imprint centre,
     shown only while a move-drag is actually snapped on that axis. */
  function showCentreGuides(showV, showH) {
    if (!dom.imprint) return;
    let g = dom.centreGuides;
    if (!g) {
      g = dom.centreGuides = {
        v: el("div", { class: "nkd__centre-guide nkd__centre-guide--v" }),
        h: el("div", { class: "nkd__centre-guide nkd__centre-guide--h" }),
      };
      dom.imprint.appendChild(g.v);
      dom.imprint.appendChild(g.h);
    }
    // Diamond: the guides live inside the +45°-rotated imprint, so counter-rotate
    // them by −napkinRot to render true screen vertical/horizontal (matching the
    // screen-frame snap above). Square: clear the inline transform so the base CSS
    // (half-pixel crispness only) applies — unchanged.
    const rot = napkinRotation();
    g.v.style.transform = rot
      ? "translateX(-0.5px) rotate(" + -rot + "deg)"
      : "";
    g.h.style.transform = rot
      ? "translateY(-0.5px) rotate(" + -rot + "deg)"
      : "";
    g.v.style.display = showV ? "block" : "none";
    g.h.style.display = showH ? "block" : "none";
  }
  function onDragUp() {
    if (!drag) return;
    showCentreGuides(false, false);
    if (drag.mode === "rotate") {
      State.rotatingAngle = null;
      if (dom.anglePill) {
        dom.anglePill.remove();
        dom.anglePill = null;
      }
    }
    drag = null;
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragUp);
    window.removeEventListener("pointercancel", onDragUp);
  }

  /* ============================================================== preview paint */
  /* Editor is axis-aligned for every product. Size is bounded by the stage's inner area
     (stage W/H minus padding + eyebrow + caption + tools), capped at 460 px so the napkin
     doesn't dominate huge viewports. */
  const STAGE_HORIZ_PAD = 96; // 48 each side
  const STAGE_VERT_RESERVE = 240; // top pad + eyebrow + caption + tools + bottom pad + gaps
  const NAPKIN_MAX_PX = 540;
  const WRAP_HALO = 48; // breathing room around the rotated diamond's tips (total, both sides)
  // The wrap container holds the rotated diamond (or axis-aligned square) tip-to-tip.
  // For square + diamond to render at the SAME visible side length, the wrap must always
  // be sized for the diamond's bounding box. Per-side margin = napkinSidePx × (√2 − 1) / 2
  // (the rotated tip extension) plus half the halo. We ceil the per-side margin to an
  // INTEGER so (wrap − napkin) is always even, which keeps the flex-centred napkin's
  // geometric midline aligned to the integer pixel where the SVG crosshair draws — no
  // sub-pixel W-E / N-S offset.
  function halfMarginFor(napkinSidePx) {
    return Math.ceil((napkinSidePx * (Math.SQRT2 - 1)) / 2 + WRAP_HALO / 2);
  }
  function wrapPxFor(napkinSidePx) {
    return napkinSidePx + 2 * halfMarginFor(napkinSidePx);
  }
  function currentNapkinDisplaySize() {
    // Size from the VIEWPORT, never from the stage. The stage's clientHeight depends on
    // our own wrap size — reading it creates a feedback loop that grows the napkin on
    // every paint. Viewport dimensions are stable across re-renders.
    // Layout-viewport width, NOT window.innerWidth: on iOS Safari innerWidth tracks
    // the VISUAL viewport, which narrows under pinch zoom AND under the auto-zoom
    // Safari applies when focusing a sub-16px text input; that fired `resize` and
    // re-sized the napkin mid-edit. documentElement.clientWidth ignores zoom, so the
    // computed size only changes on a real width/orientation change.
    const vw =
      document.documentElement.clientWidth || window.innerWidth || 1280;
    const vh = window.innerHeight || 900;
    // Desktop is a side-by-side rail + stage; mobile (≤899px, see the CSS @media) stacks the
    // rail ABOVE the stage, so the stage spans the full viewport minus its 14px side padding.
    // Subtracting the 360px rail on mobile shrank usableW, under-sized the napkin and pushed
    // the (correctly flex-centred) wrap off to the left / overflowing. Branch on the same
    // 899px breakpoint as the CSS. (Debra 18 Jun review #14: "preview is left of center".)
    const isStacked =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 899px)").matches;
    // The extra 4px past the 28px padding is a rounding guard: the wrap can finish
    // up to ~2px over usableW (each per-side margin is ceil()'d in halfMarginFor),
    // so under-sizing usableW by a hair keeps the wrap STRICTLY inside the stage —
    // no horizontal page overflow (the "scroll sideways into empty space" bug).
    const usableW = isStacked
      ? vw - 32 /* 2 × 14px mobile stage padding + 4px rounding guard */
      : vw - 360 /* rail */ - STAGE_HORIZ_PAD;
    // Binding constraint. Desktop's stage is sticky and must fit the viewport HEIGHT,
    // so the napkin is bounded by the smaller of width/height. Mobile STACKS and the
    // page scrolls (the stage is position:static at ≤899px), so the napkin only needs
    // to fit the WIDTH. Reading window.innerHeight on mobile made the preview "breathe"
    // on every scroll: iOS/Android show/hide the address bar as you scroll, which fires
    // a `resize` event and swings innerHeight ~60–100px, so Math.min(usableW, usableH)
    // kept flipping its binding dimension and re-sized the napkin mid-scroll. Width-only
    // on mobile = a size that depends on nothing that changes during a scroll. (vh is
    // still read for the desktop branch.)
    const limit = isStacked
      ? usableW
      : Math.min(
          usableW,
          vh - 280 /* navbar + stage paddings + caption + tools */,
        );
    // maxSide caps the napkin so its diamond wrap (≈ side×√2 + halo) fits `limit` —
    // that is the overflow guard; once `limit` is correct the wrap can no longer spill.
    const maxSide = (limit - WRAP_HALO) / Math.SQRT2;
    let s = Math.floor(Math.min(maxSide, NAPKIN_MAX_PX));
    if (s % 2 !== 0) s -= 1;
    // Lower floor on mobile so a narrow phone's diamond can shrink to fit rather than being
    // pinned at 220 (whose wrap ≈ 359px overflows a ~292–332px phone stage).
    return clamp(s, isStacked ? 140 : 220, NAPKIN_MAX_PX);
  }
  // Last napkin size paintPreview rendered at. The resize handler reads this to skip a
  // full re-layout when the viewport change doesn't actually change the napkin size
  // (e.g. a mobile address-bar scroll, which now leaves the width-only size untouched).
  let lastPaintSize = null;
  function paintPreview() {
    if (!dom.napkin) return;
    const size = currentNapkinDisplaySize();
    lastPaintSize = size;
    const L = NKD.geo.layout(State.product, size);
    const isDiamond = State.product.shape === "diamond";

    // Wrap = bounding square. On diamond it's size × √2 (+ halo) so the rotated napkin
    // fits without clipping the tips. The napkin is flex-centred inside the wrap.
    // Wrap is constant size for ALL shapes/products — sized for the rotated diamond's
    // tip-to-tip diagonal. The napkin (always size × size) is flex-centred inside the
    // wrap and rotates in place. Switching between Square and Diamond is a pure rotation;
    // wrap dimensions and the napkin's screen centre are invariant.
    const wrapSize = wrapPxFor(size);
    const wrap = dom.previewWrap;
    wrap.style.width = wrapSize + "px";
    wrap.style.height = wrapSize + "px";

    // Napkin: natural size×size, rotated 45° on diamond. Imprint + safe-zone are children
    // so they rotate with the sheet. The NSEW grid is inserted at wrap-level instead so
    // it stays screen-axis-aligned (handled below).
    dom.napkin.style.width = size + "px";
    dom.napkin.style.height = size + "px";
    dom.napkin.setAttribute("data-shape", State.product.shape);
    // Cocktail: FSN's product photo IS the napkin surface (hex underneath while it
    // loads). QF stays flat hex, the supplied photos are unfolded napkins, and the
    // QF preview is a folded quadrant with crease hints, so a flat-lay photo would
    // misrepresent the finished fold. Diamond needs no special-casing: the photo
    // rotates with the napkin box, exactly like the physical napkin would.
    dom.napkin.style.background = L.isQF
      ? (State.napkinColor && State.napkinColor.hex) || "#fff"
      : napkinSurfaceCSS(State.napkinColor, false);
    dom.napkin.style.transform = isDiamond ? "rotate(45deg)" : "";
    dom.napkin.style.transformOrigin = "center center";

    // Imprint: child of the napkin, positioned at the asymmetric fold-corner origin.
    // Rotates with the napkin on diamond. On QF the imprint is pinned into the fold
    // corner (top + left flush to the cloth); on cocktail it is centred.
    Object.assign(dom.imprint.style, {
      left: L.imprintOffsetX + "px",
      top: L.imprintOffsetY + "px",
      width: L.imprintPx + "px",
      height: L.imprintPx + "px",
    });

    // Wipe any pre-existing safe-zone / grid / fold overlays — paintPreview is the single
    // source of truth for re-inserting them. (No stitch overlay: FSN's napkins are ordered
    // with no stitch line, per Debra — do not add one to the preview.)
    $$(".nkd__svg-safe", wrap).forEach((n) => n.remove());
    $$(".nkd__svg-grid", wrap).forEach((n) => n.remove());
    $$(".nkd__svg-fold", wrap).forEach((n) => n.remove());

    // Safe-zone hatch — child of the napkin, so it rotates with the sheet on diamond.
    // Hatches the real 0.25" non-printable margin on the FINISHED edges only. On QF the
    // fold edges run flush to the cloth (no margin), so nothing is hatched there (the fold
    // crease is drawn separately below). On cocktail all four edges are hatched.
    if (State.showSafeZone) {
      dom.napkin.insertBefore(
        buildSafeZoneSVG(
          size,
          L.imprintOffsetX,
          L.imprintOffsetY,
          L.imprintPx,
          L.insets,
          safeZoneHex(State.napkinColor && State.napkinColor.hex),
        ),
        dom.imprint,
      );
      // Fold crease indicators on QF's two fold edges. Subtle, and grouped with the
      // safe-zone toggle so one switch clears every on-surface guide (the team is
      // sensitive to on-surface guide colour, per the 11-13 Jun review notes).
      if (L.isQF) {
        dom.napkin.insertBefore(
          buildFoldHintSVG(
            size,
            L,
            safeZoneHex(State.napkinColor && State.napkinColor.hex),
          ),
          dom.imprint,
        );
      }
    }
    // NSEW alignment grid: wrap-level overlay, screen-axis-aligned, sized to the bounding
    // wrap so the marks sit outside the napkin tips on diamond. Does NOT rotate with the
    // sheet — the customer always reads N/E/S/W in screen frame regardless of shape.
    if (State.showGrid) {
      const grid = buildGridSVG(wrapSize);
      wrap.insertBefore(grid, wrap.firstChild);
    }

    // layers
    renderLayers();

    // captions
    dom.stageEyebrow.textContent =
      State.product.sizeLabel || sizeLabelFor(State.product);
    if (State.napkinColor) {
      dom.capName.textContent = State.napkinColor.name;
      dom.capSub.textContent = " — " + (State.napkinColor.sub || "");
    }
    dom.safeToggle.disabled = false;
    dom.safeToggle.setAttribute("aria-pressed", String(State.showSafeZone));
    dom.gridToggle.setAttribute("aria-pressed", String(State.showGrid));

    // Diamond badge no longer needed — the editor itself shows the diamond presentation.
    // Removed `paintDiamondBadge(size)` call. The function is left in place for now in
    // case we want to bring it back as a confirmatory thumbnail.
    const existingBadge = dom.previewWrap.parentNode
      ? $(".nkd__diamond-badge", dom.previewWrap.parentNode)
      : null;
    if (existingBadge) existingBadge.remove();
  }

  // Renders a small badge in the editor corner showing "Diamond presentation" + a tiny
  // 45°-rotated thumbnail of the napkin, so the customer remembers the chosen orientation
  // even though the editor itself is axis-aligned. No-op on square products.
  function paintDiamondBadge(napkinSize) {
    if (!dom.previewWrap) return;
    const existing = $(".nkd__diamond-badge", dom.previewWrap.parentNode);
    if (existing) existing.remove();
    if (State.product.shape !== "diamond") return;
    const stage = dom.previewWrap.parentNode;
    if (!stage) return;
    const thumb = el("div", { class: "nkd__diamond-badge__thumb" });
    // chip photo (square, unrotated) so the badge matches the swatch the customer picked
    thumb.style.background = napkinSurfaceCSS(State.napkinColor, true);
    const badge = el("div", { class: "nkd__diamond-badge" }, [
      thumb,
      el("div", { class: "nkd__diamond-badge__txt" }, [
        el("span", {
          class: "nkd__diamond-badge__lbl",
          text: "Will be presented",
        }),
        el("span", { class: "nkd__diamond-badge__val", text: "Diamond" }),
      ]),
    ]);
    stage.appendChild(badge);
  }
  // Editor guide colour for the ALIGNMENT GRID + QF fold hints + centre-snap lines.
  // High-contrast aqua so those guides stay visible on every napkin colour, dark ones
  // included. (The safe-zone/unprinted-area guide no longer uses this — see safeZoneHex.)
  const GUIDE_HEX = "#00b8d9";
  // Safe-zone (unprinted-area) guide colour: BLACK, switching to WHITE on black / near-black
  // napkins so it stays visible. Debra 18 Jun review #1: "Replace Blue unprinted area with
  // Black. On Black napkins use White." Decided from the napkin hex's perceptual luminance
  // (sRGB 0.2126/0.7152/0.0722; threshold 0.4 → dark napkins get white). Scope: safe zone
  // only — the alignment grid + fold hints deliberately keep GUIDE_HEX.
  function safeZoneHex(napkinHex) {
    const h = String(napkinHex || "#ffffff").replace("#", "");
    const full =
      h.length === 3 ? h.replace(/(.)/g, "$1$1") : (h + "ffffff").slice(0, 6);
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return lum < 0.4 ? "#ffffff" : "#000000";
  }
  function svg(tag, attrs, kids) {
    const node = document.createElementNS("http://www.w3.org/2000/svg", tag);
    if (attrs)
      for (const k in attrs)
        if (attrs[k] != null) node.setAttribute(k, attrs[k]);
    if (kids)
      (Array.isArray(kids) ? kids : [kids]).forEach(
        (c) => c && node.appendChild(c),
      );
    return node;
  }
  function buildSafeZoneSVG(size, offX, offY, impPx, insets, guideHex) {
    // Hatches the ring between the napkin (size x size at 0,0) and the imprint rect
    // (impPx x impPx at offX,offY) = the non-printable margin. The mask leaves only the
    // ring visible, so on QF the flush fold edges (offX/offY = 0) get NO hatch by
    // construction and only the finished edges are hatched. The boundary line is drawn
    // per finished edge (inset > 0) so QF shows it on the right + bottom only; cocktail
    // gets all four (a full rect, exactly as before).
    const ins = insets || { top: 0.25, right: 0.25, bottom: 0.25, left: 0.25 };
    // Safe-zone colour comes from the caller (black, or white on dark napkins); GUIDE_HEX
    // is only a defensive fallback if no colour is passed.
    const gc = guideHex || GUIDE_HEX;
    const uid = "nkd-hatch-" + Math.random().toString(36).slice(2, 7);
    const root = svg("svg", {
      class: "nkd__svg-safe",
      width: size,
      height: size,
      style: "position:absolute;left:0;top:0;pointer-events:none",
    });
    const defs = svg("defs");
    const pat = svg("pattern", {
      id: uid,
      width: 6,
      height: 6,
      patternUnits: "userSpaceOnUse",
      patternTransform: "rotate(45)",
    });
    pat.appendChild(
      svg("line", {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 6,
        stroke: gc,
        "stroke-opacity": 0.5,
        "stroke-width": 1.2,
      }),
    );
    const mask = svg("mask", { id: uid + "-m" });
    mask.appendChild(
      svg("rect", { x: 0, y: 0, width: size, height: size, fill: "white" }),
    );
    mask.appendChild(
      svg("rect", {
        x: offX,
        y: offY,
        width: impPx,
        height: impPx,
        fill: "black",
      }),
    );
    defs.appendChild(pat);
    defs.appendChild(mask);
    root.appendChild(defs);
    root.appendChild(
      svg("rect", {
        x: 0,
        y: 0,
        width: size,
        height: size,
        fill: "url(#" + uid + ")",
        mask: "url(#" + uid + "-m)",
      }),
    );
    // Inner boundary line on each FINISHED edge only (a fold edge has no margin to bound).
    const x0 = offX,
      y0 = offY,
      x1 = offX + impPx,
      y1 = offY + impPx;
    const edge = (a, b, c, d) =>
      svg("line", {
        x1: a,
        y1: b,
        x2: c,
        y2: d,
        stroke: gc,
        "stroke-opacity": 0.9,
        "stroke-width": 1.25,
      });
    if (ins.top > 0) root.appendChild(edge(x0, y0, x1, y0));
    if (ins.bottom > 0) root.appendChild(edge(x0, y1, x1, y1));
    if (ins.left > 0) root.appendChild(edge(x0, y0, x0, y1));
    if (ins.right > 0) root.appendChild(edge(x1, y0, x1, y1));
    return root;
  }
  // Fold crease indicators for the 1/4 Fold napkin: a faint dashed line hugging each of
  // the two fold edges + a small "fold" label. Child of the napkin, so it rotates with the
  // sheet on diamond. Uses the SAME colour as the safe zone (safeZoneHex: black, white on
  // dark napkins) so the QF guide set reads consistently and honours Debra 18 Jun #1/#2
  // ("no blue on the napkin"). Low opacity + thin dashes + two-edges-only keep it subtle.
  // (The alignment grid stays GUIDE_HEX deliberately: it is opt-in/default-off.)
  function buildFoldHintSVG(size, L, guideHex) {
    const gc = guideHex || GUIDE_HEX; // defensive fallback only
    const f = NKD.geo.foldEdges(L.foldCorner || NKD.geo.FOLD_CORNER);
    const root = svg("svg", {
      class: "nkd__svg-fold",
      width: size,
      height: size,
      style: "position:absolute;left:0;top:0;pointer-events:none",
    });
    const PAD = 7; // px inset from the cloth edge so the crease reads as a fold, not a border
    const crease = (a, b, c, d) =>
      svg("line", {
        x1: a,
        y1: b,
        x2: c,
        y2: d,
        stroke: gc,
        "stroke-opacity": 0.4,
        "stroke-width": 1,
        "stroke-dasharray": "2 5",
      });
    const label = (x, y, rot) => {
      const t = svg("text", {
        x: x,
        y: y,
        "text-anchor": "middle",
        "dominant-baseline": "central",
        fill: gc,
        opacity: 0.55,
        "font-family": "Poppins, sans-serif",
        "font-size": 8,
        "font-weight": 600,
        transform: rot ? "rotate(" + rot + " " + x + " " + y + ")" : null,
      });
      t.textContent = "fold";
      return t;
    };
    if (f.top) {
      root.appendChild(crease(0, PAD, size, PAD));
      root.appendChild(label(size / 2, PAD + 7, 0));
    }
    if (f.bottom) {
      root.appendChild(crease(0, size - PAD, size, size - PAD));
      root.appendChild(label(size / 2, size - PAD - 7, 0));
    }
    if (f.left) {
      root.appendChild(crease(PAD, 0, PAD, size));
      root.appendChild(label(PAD + 7, size / 2, -90));
    }
    if (f.right) {
      root.appendChild(crease(size - PAD, 0, size - PAD, size));
      root.appendChild(label(size - PAD - 7, size / 2, 90));
    }
    return root;
  }
  function buildGridSVG(size) {
    // Wrap-level overlay: crosshair through centre + N/E/S/W marks at the four
    // edges. Default-OFF (client review 2026-06-13) so no aqua sits on the napkin
    // surface unless the customer switches the grid on; when on, the crosshair
    // covers the full napkin for centre alignment.
    // On square the wrap is the napkin size (marks sit on the napkin perimeter); on
    // diamond the wrap is larger than the napkin (marks sit just outside the tips).
    const gc = GUIDE_HEX; // visible on every napkin colour (incl. dark)
    const root = svg("svg", {
      class: "nkd__svg-grid",
      width: size,
      height: size,
      style: "position:absolute;left:0;top:0;pointer-events:none;z-index:3",
    });
    root.appendChild(
      svg("line", {
        x1: size / 2,
        y1: 0,
        x2: size / 2,
        y2: size,
        stroke: gc,
        "stroke-width": 1,
        "stroke-opacity": 0.65,
        "stroke-dasharray": "4 4",
      }),
    );
    root.appendChild(
      svg("line", {
        x1: 0,
        y1: size / 2,
        x2: size,
        y2: size / 2,
        stroke: gc,
        "stroke-width": 1,
        "stroke-opacity": 0.65,
        "stroke-dasharray": "4 4",
      }),
    );
    const marks = [
      { c: "N", x: size / 2, y: 9 },
      { c: "E", x: size - 9, y: size / 2 },
      { c: "S", x: size / 2, y: size - 9 },
      { c: "W", x: 9, y: size / 2 },
    ];
    marks.forEach((m) => {
      const t = svg("text", {
        x: m.x,
        y: m.y,
        "text-anchor": "middle",
        "dominant-baseline": "central",
        fill: gc,
        opacity: 0.7,
        "font-family": "Poppins, sans-serif",
        "font-size": 9,
        "font-weight": 700,
      });
      t.textContent = m.c;
      root.appendChild(t);
    });
    return root;
  }

  /* ================================================================ export */
  /* Authoritative canvas render — mirrors the editor's IMPRINT frame exactly:
       1. Draw the napkin background AXIS-ALIGNED at (0,0)-(napkinSidePx, napkinSidePx).
       2. Clip + translate to the imprint frame (axis-aligned within the napkin).
       3. Draw the layers using their stored rotation (imprint frame).
     `box.rotation` is now stored in imprint frame for ALL products (square stores 0 by
     default, diamond stores -45 by default — see `ensureBoxes`). The diamond's on-table
     orientation falls out by construction: cloth folded +45° on table, design at -45° in
     cloth/imprint frame → 0° upright on the table. The print PDF therefore writes the
     imprint-frame canvas exactly as rendered, with no extra rotation pass.
     The proof modal continues to CSS-rotate the rendered canvas +45° on diamond so the
     customer sees the table presentation in the final-review modal. */
  function renderToCanvas(canvas, napkinSidePx, opts) {
    opts = opts || {};
    const n = NKD.geo.napkin(State.product);
    canvas.width = napkinSidePx;
    canvas.height = napkinSidePx;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, napkinSidePx, napkinSidePx);

    // (1) Napkin background — skipped when bg === null (transparent print file).
    if (opts.bg !== null) {
      ctx.fillStyle =
        opts.bg || (State.napkinColor && State.napkinColor.hex) || "#ffffff";
      ctx.fillRect(0, 0, napkinSidePx, napkinSidePx);
    }

    // (2) Imprint + design layers. Asymmetric origin (fold-corner pinned on QF) so the
    // export matches the editor preview and the backend engine exactly. The imprint is
    // square (impPx) in every current config; only its origin shifts.
    const pxPerIn = napkinSidePx / n.napkinIn;
    const impPx = n.imprintIn * pxPerIn;
    const offX = n.insets.left * pxPerIn;
    const offY = n.insets.top * pxPerIn;
    ctx.save();
    ctx.beginPath();
    ctx.rect(offX, offY, impPx, impPx);
    ctx.clip();
    ctx.translate(offX, offY);

    // `diamondTablePresentation` is retained as a no-op for back-compat with the call
    // sites — the imprint-frame storage convention now encodes the print orientation
    // directly via `box.rotation` (default -45° on diamond), so no extra rotation pass
    // is needed. The proof modal still CSS-rotates the rendered canvas at display time.
    void opts.diamondTablePresentation;

    if (State.logo && State.logoBox && opts.logoImg)
      drawImgLayer(ctx, opts.logoImg, State.logoBox, impPx);
    State.texts.forEach((t) => {
      if (t.text && t.box) drawTextLayer(ctx, t, impPx);
    });

    ctx.restore();
  }
  function drawTextLayer(ctx, item, impPx) {
    const box = item.box;
    ctx.save();
    const cx = (box.x + box.w / 2) * impPx;
    const cy = (box.y + box.h / 2) * impPx;
    ctx.translate(cx, cy);
    ctx.rotate(((box.rotation || 0) * Math.PI) / 180);
    const f = item.font || DATA.fonts[0] || { family: "Work Sans" };
    const fontPx = Math.max(6, (box.fontFrac || 0.12) * impPx);
    ctx.font =
      (f.style === "italic" ? "italic " : "") +
      fontPx +
      'px "' +
      f.family +
      '", serif';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const boxW = box.w * impPx - 12; // matches the 6px side padding in the DOM layer
    const lines = wrapText(ctx, item.text, boxW);
    const lh = fontPx * 1.1;
    const fillStyle = State.foil
      ? foilGradientFor(
          ctx,
          State.foilColor && State.foilColor.id,
          box.w * impPx,
        )
      : previewInkHex();
    ctx.fillStyle = fillStyle;
    const startY = -((lines.length - 1) * lh) / 2;
    lines.forEach((ln, i) => ctx.fillText(ln, 0, startY + i * lh));
    ctx.restore();
  }
  function wrapText(ctx, text, maxW) {
    const out = [];
    String(text)
      .split(/\n/)
      .forEach((para) => {
        const words = para.split(/\s+/).filter(Boolean);
        if (!words.length) {
          out.push("");
          return;
        }
        let line = words[0];
        for (let i = 1; i < words.length; i++) {
          const test = line + " " + words[i];
          if (ctx.measureText(test).width > maxW && line) {
            out.push(line);
            line = words[i];
          } else line = test;
        }
        out.push(line);
      });
    return out;
  }
  function drawImgLayer(ctx, img, box, impPx) {
    ctx.save();
    const cx = (box.x + box.w / 2) * impPx;
    const cy = (box.y + box.h / 2) * impPx;
    const w = box.w * impPx,
      h = box.h * impPx;
    ctx.translate(cx, cy);
    ctx.rotate(((box.rotation || 0) * Math.PI) / 180);
    // "contain" fit inside w×h
    const ar = img.naturalWidth / Math.max(1, img.naturalHeight);
    let dw = w,
      dh = w / ar;
    if (dh > h) {
      dh = h;
      dw = h * ar;
    }
    if (State.logo && State.logo.opaque && !State.foil) {
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    } else {
      // recolour: alpha of the artwork → ink/foil
      const tmp = el("canvas");
      tmp.width = Math.max(1, Math.round(dw));
      tmp.height = Math.max(1, Math.round(dh));
      const tctx = tmp.getContext("2d");
      tctx.drawImage(img, 0, 0, tmp.width, tmp.height);
      tctx.globalCompositeOperation = "source-in";
      if (State.foil) {
        tctx.fillStyle = foilGradientFor(
          tctx,
          State.foilColor && State.foilColor.id,
          tmp.width,
        );
      } else {
        tctx.fillStyle = previewInkHex();
      }
      tctx.fillRect(0, 0, tmp.width, tmp.height);
      ctx.drawImage(tmp, -dw / 2, -dh / 2, dw, dh);
    }
    ctx.restore();
  }
  function foilGradientFor(ctx, which, w) {
    const g = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
    if (which === "silver") {
      g.addColorStop(0, "#e8e8e8");
      g.addColorStop(0.5, "#a8a8a8");
      g.addColorStop(1, "#e8e8e8");
    } else {
      g.addColorStop(0, "#ead489");
      g.addColorStop(0.5, "#cda64f");
      g.addColorStop(1, "#ead489");
    }
    return g;
  }
  // load the uploaded artwork as an <img> (resolves null if none / fails)
  function loadLogoImg() {
    return new Promise((res) => {
      if (!State.logo) return res(null);
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => res(null);
      img.src = State.logo.src;
    });
  }

  /* ================================================================== modal */
  function buildModal() {
    const previewSlot = el("div", { class: "nkd__modal-preview" });
    const summaryDL = el("dl", { "data-modal-summary": "" });
    const termsInput = el("input", { type: "checkbox" });
    const addBtn = el(
      "button",
      {
        class: "nkd__btn nkd__btn--primary nkd__modal-add",
        type: "button",
        disabled: true,
      },
      "Add to Cart",
    );
    termsInput.addEventListener("change", () => {
      addBtn.disabled = !termsInput.checked;
      if (termsInput.checked && !State.termsAcceptedAt) {
        // capture the legal-record timestamp on first tick of the current modal session.
        // resets on every openModal so a re-open requires a fresh tick.
        State.termsAcceptedAt = new Date().toISOString();
      }
    });
    addBtn.addEventListener("click", () => handleAddToCart(addBtn));
    const node = el(
      "div",
      {
        class: "nkd__modal",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": "Design preview",
      },
      [
        el("div", { class: "nkd__modal-card" }, [
          el("button", {
            class: "nkd__modal-close",
            type: "button",
            "aria-label": "Close",
            text: "×",
            onclick: () => closeModal(),
          }),
          el("div", { class: "nkd__modal-eyebrow", text: "Final preview" }),
          el("h2", { class: "nkd__modal-title", text: "Your napkin" }),
          previewSlot,
          el("div", { class: "nkd__modal-summary" }, [summaryDL]),
          el("div", { class: "nkd__modal-delivery" }, [
            el("span", {
              class: "nkd__modal-delivery-label",
              text: "Printed to order",
            }),
            el("p", {
              class: "nkd__modal-delivery-text",
              text: "Please allow up to 4 weeks for production and delivery. We'll email your tracking details the moment your napkins are printed and dispatched.",
            }),
          ]),
          el("label", { class: "nkd__terms" }, [
            termsInput,
            el("span", {
              html: 'I have reviewed my design and agree to the <a href="/policies/terms-of-service" target="_blank" rel="noopener">Terms &amp; Conditions</a>. This is my approved proof.',
            }),
          ]),
          el("div", { class: "nkd__modal-actions" }, [
            el("button", {
              class: "nkd__btn nkd__btn--ghost",
              type: "button",
              text: "Keep editing",
              onclick: () => closeModal(),
            }),
            addBtn,
          ]),
        ]),
      ],
    );
    node.addEventListener("click", (e) => {
      if (e.target === node) closeModal();
    });
    // a11y: trap Tab focus within the dialog while open (registered once — buildModal runs once).
    node.addEventListener("keydown", (e) => {
      if (e.key !== "Tab") return;
      const f = Array.from(
        node.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((x) => !x.disabled && x.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
    return { node, previewSlot, summaryDL, termsInput, addBtn };
  }
  function summaryRows() {
    const key = State.product.key;
    const country = (CFG.market && CFG.market.country) || "US";
    const coef = marketCoefficient(country);
    const perNapkin = unitPriceUSD(key, State.qty) * coef;
    const foilTotal = State.foil ? State.qty * FOIL_SURCHARGE() * coef : 0;
    const sampleTotal =
      State.sample && State.qty >= SAMPLE_MIN() ? SAMPLE_PRICE() : 0;
    const total = State.qty * perNapkin + foilTotal + sampleTotal;
    const rows = [
      [
        "Product",
        State.product.group +
          " · " +
          (State.product.shape === "square" ? "Square" : "Diamond"),
      ],
      ["Size", State.product.sizeLabel || sizeLabelFor(State.product)],
      ["Napkin colour", State.napkinColor ? State.napkinColor.name : "—"],
      ["Finish", designColourLabel()],
    ];
    const proofTexts = State.texts.filter((t) => t.text.trim());
    proofTexts.forEach((t, i) => {
      rows.push([
        proofTexts.length > 1 ? "Text " + (i + 1) : "Text",
        '"' + t.text + '" · ' + ((t.font && t.font.family) || ""),
      ]);
    });
    rows.push(["Artwork", State.logo ? State.logo.name : "None"]);
    rows.push(["Quantity", State.qty.toLocaleString() + " napkins"]);
    rows.push(["Per napkin", moneyUSD(perNapkin)]);
    if (State.foil && foilTotal > 0)
      rows.push(["Foil surcharge", moneyUSD(foilTotal)]);
    if (sampleTotal) rows.push(["Physical sample", moneyUSD(sampleTotal)]);
    rows.push(["Total", moneyUSD(total)]);
    return { rows, total };
  }
  /* ============================ Vector Print Pipeline (flag-gated) ===========
     Gate 1 (upload): retain the original in blob, then validate + vectorise it
     server-side. Gate 2 (Preview): generate the real single-colour vector PDF +
     proof. All inert unless CFG.vectorPipeline is ON. */
  function vppUploadOriginal(dataUrl, ext) {
    if (!CFG.azureUploadUrl)
      return Promise.reject(new Error("Upload service isn't configured."));
    return fetch(CFG.azureUploadUrl + "?ext=" + encodeURIComponent(ext), {
      method: "GET",
      credentials: "omit",
    })
      .then((r) => {
        if (!r.ok) throw new Error("SAS request failed (" + r.status + ")");
        return r.json();
      })
      .then((sas) =>
        fetch(dataUrl)
          .then((r) => r.blob())
          .then((blob) =>
            fetch(sas.uploadUrl, {
              method: "PUT",
              credentials: "omit",
              headers: {
                "x-ms-blob-type": "BlockBlob",
                "Content-Type": blob.type || "image/" + ext,
              },
              body: blob,
            }).then((r) => {
              if (!r.ok) throw new Error("upload failed (" + r.status + ")");
              return sas.blobUrl;
            }),
          ),
      );
  }
  function vppVectorize(dataUrl, ext) {
    if (!CFG.vectorizeArtworkUrl)
      return Promise.reject(new Error("Vectorise service isn't configured."));
    return vppUploadOriginal(dataUrl, ext).then((blobUrl) =>
      fetch(CFG.vectorizeArtworkUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ artworkUrl: blobUrl, ext: ext }),
      }).then((r) =>
        r.json().then((j) => Object.assign({ originalUrl: blobUrl }, j)),
      ),
    );
  }
  function vppPlacement() {
    const filledTexts = State.texts.filter((t) => t.text && t.text.trim());
    const geo = NKD.geo.napkin(State.product);
    return {
      product: {
        group: State.product.group,
        shape: State.product.shape,
      },
      // The four per-edge non-printable insets (inches), the single source of truth for
      // the printable quadrant. The engine clips the proof + print PDF to exactly these,
      // so preview === proof === print. QF pins the imprint into the fold corner (two
      // edges at 0); cocktail is symmetric BLEED. foldCorner travels for the record.
      insets: geo.insets,
      foldCorner: geo.foldCorner,
      // Display-only: the PROOF is rendered in the chosen ink colour so the
      // approval modal matches the editor. The delivered PDF stays solid black
      // (single-colour separation) — the printer applies this ink, which also
      // travels on the PO as _Design Color / _Pantone.
      // Foil: the server proof takes one flat hex, so use the gradient's mid
      // tone (gold/silver) — otherwise a foil proof renders near-black.
      inkHex: State.foil
        ? (State.foilColor && State.foilColor.id) === "silver"
          ? "#a8a8a8"
          : "#cda64f"
        : previewInkHex(),
      // Display-only: render the proof on the selected napkin colour so it looks
      // like the real napkin (the editor canvas already does). PDF is unaffected.
      napkinHex: (State.napkinColor && State.napkinColor.hex) || "#ffffff",
      // Display-only: the real napkin stock photo (same theme asset the editor
      // paints, 2026-07-02), so the server proof matches the editor surface.
      // Cocktail only: QF previews as a folded quadrant on the flat hex, and the
      // photos are unfolded napkins. The engine host-validates the URL and falls
      // back to napkinHex on any problem. PDF is unaffected either way.
      napkinPhotoUrl: (function () {
        if (geo.isQF) return null;
        var u = napkinPhotoUrl(State.napkinColor, false);
        if (!u) return null;
        return u.indexOf("//") === 0 ? "https:" + u : u;
      })(),
      texts: filledTexts.map((t) => ({
        box: t.box,
        content: t.text,
        font: (t.font && t.font.family) || "",
      })),
      artwork:
        State.logo && State.logo.vector && State.logoBox
          ? {
              box: State.logoBox,
              d: State.logo.vector.d,
              w: State.logo.vector.w,
              h: State.logo.vector.h,
            }
          : null,
    };
  }
  function generateVectorProof() {
    if (!CFG.generateVectorUrl)
      return Promise.reject(new Error("Generate service isn't configured."));
    return fetch(CFG.generateVectorUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(vppPlacement()),
    }).then((r) => r.json());
  }

  function openModal() {
    ensureBoxes();
    const m = dom.modal;
    // Gate-2 session token: a slow generateVectorProof() response from a PREVIOUS
    // modal open must not touch this session (stale proof img, stale print URL,
    // premature T&C enable). Each open/close bumps the token; async handlers
    // bail out unless they still hold the current one.
    const gen = (State._vppGen = (State._vppGen || 0) + 1);
    m.termsInput.checked = false;
    m.addBtn.disabled = true;
    State.termsAcceptedAt = null; // fresh modal session = fresh consent capture
    // Vector Print Pipeline: fresh gate session = discard any prior generated file.
    State.vectorPrintUrl = null;
    State.vectorProofUrl = null;
    if (CFG.vectorPipeline) m.termsInput.disabled = true; // re-enabled only once the proof generates
    // summary
    const { rows, total } = summaryRows();
    m.summaryDL.innerHTML = "";
    rows.forEach(([k, v]) => {
      m.summaryDL.appendChild(el("dt", { text: k }));
      m.summaryDL.appendChild(el("dd", { text: v }));
    });
    m.addBtn.textContent = "Add to Cart — " + moneyUSD(total);
    // clean proof render (no chrome) — from the authoritative export canvas. The render
    // itself is axis-aligned (same code path as print and editor); for diamond products
    // the canvas is then CSS-rotated 45° so the customer sees the table presentation.
    m.previewSlot.innerHTML = "";
    const isDiamond = State.product.shape === "diamond";
    const proof = el("canvas", {
      style: {
        width: "min(360px, 70vw)",
        height: "auto",
        boxShadow: "0 18px 40px -14px rgba(28,21,15,0.28)",
        transform: isDiamond ? "rotate(45deg)" : "none",
        transformOrigin: "center center",
        // Reserve vertical space for the diamond's tip extension. Geometry: a square of
        // side `s` rotated 45° has bounding box side `s × √2`, so the tip extends
        // `s × (√2 − 1) / 2 ≈ s × 0.2071` past the original top and bottom. + 12px halo.
        margin: isDiamond ? "calc((min(360px, 70vw) * 0.2071) + 12px) 0" : "0",
      },
    });
    m.previewSlot.appendChild(proof);
    if (CFG.vectorPipeline) {
      // Gate 2: generate the real single-colour vector PDF + proof server-side.
      // What the customer sees here IS a render of the file that will print.
      proof.style.display = "none";
      // Branded "generating" state: spinner + label (same spinner as the upload gate).
      const status = el("div", { class: "nkd__modal-genstatus" }, [
        nkdSpinner(),
        el("p", {
          class: "nkd__modal-genstatus-label",
          text: "Generating your print-ready proof…",
        }),
      ]);
      m.previewSlot.appendChild(status);
      // Swap the status block to the branded error panel (consistent with Gate 1).
      const showGenError = (msg) => {
        status.setAttribute("data-error", "");
        status.innerHTML = "";
        status.appendChild(
          el("div", { class: "nkd__upload-error", role: "alert" }, [
            el("span", { class: "nkd__upload-error-ic", html: NKD_WARN_SVG }),
            el("span", { class: "nkd__upload-error-msg", text: msg }),
          ]),
        );
        m.termsInput.disabled = true; // can't approve an unfulfillable / failed proof
      };
      generateVectorProof()
        .then((res) => {
          if (gen !== State._vppGen) return; // stale Gate-2 response — newer session owns the modal
          if (res && res.ok) {
            State.vectorPrintUrl = res.printUrl;
            State.vectorProofUrl = res.proofUrl;
            status.remove();
            const img = el("img", {
              src: res.proofDataUrl || res.proofUrl,
              alt: "Print proof",
              style: {
                width: "min(360px, 70vw)",
                height: "auto",
                boxShadow: "0 18px 40px -14px rgba(28,21,15,0.28)",
                transform: isDiamond ? "rotate(45deg)" : "none",
                transformOrigin: "center center",
                margin: isDiamond
                  ? "calc((min(360px, 70vw) * 0.2071) + 12px) 0"
                  : "0",
              },
            });
            m.previewSlot.appendChild(img);
            m.termsInput.disabled = false; // approval now possible
          } else {
            showGenError(
              (res && res.reasons && res.reasons[0]) ||
                "We couldn't prepare this design for print. Please adjust it and try again.",
            );
          }
        })
        .catch(() => {
          if (gen !== State._vppGen) return; // stale Gate-2 response
          showGenError(
            "Couldn't generate your proof just now — please close this and tap Preview again.",
          );
        });
    } else {
      // await the design fonts too — a canvas silently bakes the fallback face if
      // the real font isn't loaded when renderToCanvas draws.
      Promise.all([loadLogoImg(), ensureFontsLoaded(usedTextFonts())]).then(
        (parts) => {
          renderToCanvas(proof, NKD.geo.PROOF_PX, {
            bg: undefined,
            logoImg: parts[0],
          });
        },
      );
    }
    // a11y: remember what opened the modal, then move focus into the dialog.
    State._modalReturnFocus = document.activeElement;
    m.node.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    const focusTarget = m.node.querySelector(".nkd__modal-close");
    if (focusTarget) focusTarget.focus();
  }
  function closeModal() {
    // Invalidate any in-flight Gate-2 generation for the session being closed.
    State._vppGen = (State._vppGen || 0) + 1;
    if (dom.modal) {
      dom.modal.node.removeAttribute("data-open");
      document.body.style.overflow = "";
      // a11y: return focus to the control that opened the modal.
      const rf = State._modalReturnFocus;
      if (rf && typeof rf.focus === "function") rf.focus();
      State._modalReturnFocus = null;
    }
  }
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeModal();
  });

  /* ===================================================== add to cart */
  /* Pipeline on Add-to-Cart:
       (1) render proof PNG offscreen (the authoritative customer-approved record)
       (2) render print PDF offscreen at 600 DPI, physical size, chrome hidden, transparent bg
       (3) upload {printPdf, proof} to Azure Blob Storage via SAS — parallel.
           See uploadDesignFiles() below. Backend B1 Order Intake fetches these URLs directly.
       (4) Single ATOMIC POST /cart/add.js with items[]: designer line + (optional foil-surcharge
           line, only when foil.surcharge_per_napkin > 0 — currently 0, so omitted) + (optional
           physical sample line, $50 flat). Per BUILD-PLAN-2026-05-20: native auto-discounts handle
           tier pricing on the designer line; a foil surcharge, when configured, is a separate line
           because discounts can only subtract; sample is its own product with no discount scope.
       (5) Open the theme's CUSTOM mini-cart drawer via window.PROJ.ajaxcart.updateCart()
           (call-only, never edit the mini-cart) — it re-fetches the cart section, updates the
           count bubble, and slides the drawer open in one call.
       (6) Fallback: hard /cart redirect only if the mini-cart API isn't present.
     Any failure → toast + restore the button state. Atomic post means partial cart state is impossible. */

  /* ---- helpers: render offscreen, release immediately to avoid OOM on low-end mobile (build plan R5) */
  function renderOffscreenSquare(napkinSidePx, opts) {
    const c = document.createElement("canvas");
    renderToCanvas(c, napkinSidePx, opts);
    return c;
  }
  function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
          type || "image/png",
          quality,
        );
      } catch (e) {
        reject(e);
      }
    });
  }

  /* (1) Proof PNG: 1200px per napkin side, axis-aligned in the cloth frame (the same
        render the editor produces). For diamond products, downstream consumers display
        this PNG with a 45° rotation if they want to show "table presentation". The PNG
        itself does NOT carry that rotation — keeping the asset frame stable. */
  function renderProofPng(logoImg) {
    const canvas = renderOffscreenSquare(NKD.geo.PROOF_PX, {
      bg: undefined,
      logoImg: logoImg,
    });
    return canvasToBlob(canvas, "image/png").then((blob) => {
      canvas.width = 0;
      canvas.height = 0;
      return blob;
    });
  }

  /* (2) Print PDF: 600 DPI raster wrapped in a physical-size PDF. Transparent background
        (just the ink/foil layer). On diamond products the design orientation is already
        encoded in `box.rotation` (stored in imprint frame, default -45°), so no extra
        rotation is applied at render time — the print file shows the design at -45° in
        the cloth's flat-square frame, which reads upright on the table once the cloth is
        physically folded into a diamond (+45° physical rotation cancels the -45°). */
  function renderPrintPdf(logoImg) {
    const ex = NKD.geo.exportPx(State.product);
    const canvas = renderOffscreenSquare(ex.napkinPx, {
      bg: null, // transparent — production art only carries the design layer
      diamondTablePresentation: true, // diamond products get a -45° print rotation
      logoImg: logoImg,
    });
    // jsPDF expects a PNG (or JPEG) — render the canvas to dataURL once, then drop.
    const dataUrl = canvas.toDataURL("image/png");
    canvas.width = 0;
    canvas.height = 0;
    const napkinIn = ex.napkinIn;
    const jsPDFCtor =
      (window.jspdf && window.jspdf.jsPDF) || window.jsPDF || null;
    if (!jsPDFCtor) {
      // jsPDF not loaded (CDN slow / blocked). Fail LOUD: rejecting the promise
      // routes through handleAddToCart's .catch which toasts the message and
      // restores the button. Previously we returned a stubbed octet-stream blob
      // and let the order proceed — PrintPlace would have received a non-PDF.
      console.error(
        "[napkin-designer] jsPDF not available — refusing to upload a non-PDF placeholder.",
      );
      return Promise.reject(
        new Error(
          "The print-file library is still loading. Please try Add to Cart again in a few seconds.",
        ),
      );
    }
    const pdf = new jsPDFCtor({
      unit: "in",
      format: [napkinIn, napkinIn],
      orientation: "portrait",
      compress: true,
    });
    // PNG at 600 DPI placed edge-to-edge on the physical-size sheet.
    pdf.addImage(dataUrl, "PNG", 0, 0, napkinIn, napkinIn, undefined, "FAST");
    const blob = pdf.output("blob");
    return Promise.resolve(blob);
  }

  /* (3) Upload — REAL.
        For each file: GET ${CFG.azureUploadUrl}?ext=<ext> → { uploadUrl, blobUrl }
        Then PUT the blob to uploadUrl with x-ms-blob-type: BlockBlob.
        The blob's plain URL (no SAS) is publicly readable by the backend B1 Order Intake.
        SAS-issuing Function lives at backend/tools/upload_sas (account-key SAS, 10-min expiry,
        write-only, scoped to one blob name). Storage CORS allows fivestarnapkins.com PUTs.
        Runs the two requests in parallel — total wall time ≈ max(SAS RTT) + max(PUT RTT). */
  function uploadDesignFiles(files) {
    if (!CFG.azureUploadUrl) {
      return Promise.reject(
        new Error(
          "Designer upload service isn't configured. Please contact us and we'll place your order.",
        ),
      );
    }
    const reqSas = (ext) =>
      fetch(CFG.azureUploadUrl + "?ext=" + encodeURIComponent(ext), {
        method: "GET",
        credentials: "omit",
      }).then((r) => {
        if (!r.ok) throw new Error("SAS request failed (" + r.status + ")");
        return r.json();
      });
    const put = (uploadUrl, body, contentType, label) =>
      fetch(uploadUrl, {
        method: "PUT",
        credentials: "omit",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": contentType,
        },
        body: body,
      }).then((r) => {
        if (!r.ok)
          throw new Error(label + " upload failed (HTTP " + r.status + ")");
      });
    return Promise.all([reqSas("pdf"), reqSas("png")]).then(
      ([sasPdf, sasPng]) => {
        if (!sasPdf || !sasPdf.uploadUrl || !sasPdf.blobUrl)
          throw new Error("Couldn't get an upload URL for the print file.");
        if (!sasPng || !sasPng.uploadUrl || !sasPng.blobUrl)
          throw new Error("Couldn't get an upload URL for the proof image.");
        return Promise.all([
          put(
            sasPdf.uploadUrl,
            files.printPdf,
            "application/pdf",
            "Print file",
          ),
          put(sasPng.uploadUrl, files.proof, "image/png", "Proof image"),
        ]).then(() => ({
          designUrl: sasPdf.blobUrl,
          proofUrl: sasPng.blobUrl,
        }));
      },
    );
  }

  /* (4) Designer variant pick — handles the 4-variant cutover world
        (Cocktail - Square / Cocktail - Diamond / 1/4 Fold - Square / 1/4 Fold - Diamond)
        and the legacy 1/2-variant fallback.
        Selection logic:
          • 1 variant total → use it.
          • >1 variants → match the variant whose title contains BOTH the product group token
            ("cocktail" or "1/4 fold") AND the shape token ("square" or "diamond"). Both must
            match — matching on group alone in the 4-variant world picks the wrong shape.
            Falls back to a group-only match, then to the first variant in the pool.
        Returns null if no variants at all (caller bails). */
  function pickDesignerVariantId() {
    const vs = (CFG.variants || []).filter(Boolean);
    if (!vs.length) return null;
    // Available-first: a 422 "Product is unavailable" from /cart/add.js kills the whole flow,
    // so prefer variants the API reported as purchasable. Falls back to *any* variant if all
    // are unavailable (then the cart-add will 422 with a clear Shopify message rather than a
    // silent guess).
    const available = vs.filter((v) => v.available !== false);
    const pool = available.length ? available : vs;
    if (pool.length === 1) return pool[0].id;
    const wantedGroup = (State.product.group || "").toLowerCase(); // "cocktail" | "1/4 fold"
    const wantedShape =
      State.product.shape === "diamond" ? "diamond" : "square";
    const titleOf = (v) => (v.title || "").toLowerCase();
    const optsOf = (v) =>
      (v.options || []).map((o) => String(o || "").toLowerCase()).join(" ");
    const haystack = (v) => titleOf(v) + " " + optsOf(v);
    // Primary: group AND shape both present in title/options.
    const exact = pool.find((v) => {
      const h = haystack(v);
      return (
        wantedGroup &&
        h.indexOf(wantedGroup) !== -1 &&
        h.indexOf(wantedShape) !== -1
      );
    });
    if (exact) return exact.id;
    // Fallback 1: group-only match (legacy 2-variant world).
    const groupOnly = pool.find(
      (v) => wantedGroup && haystack(v).indexOf(wantedGroup) !== -1,
    );
    if (groupOnly) {
      console.warn(
        "[napkin-designer] variant picker: no exact group+shape match; falling back to group-only:",
        groupOnly.title,
      );
      return groupOnly.id;
    }
    // Fallback 2: first variant in pool. Logged loudly so drift surfaces in QA.
    console.warn(
      "[napkin-designer] variant picker: no group match at all; using first variant:",
      pool[0].title,
    );
    return pool[0].id;
  }

  /* §A7 property bag — the locked frontend↔backend contract that the Azure Logic Apps (B1 Order
     Intake et al) consume to fulfil the order. Names match build-plan §A7/§A9; ordering kept
     stable so cart-view diffs are readable for the operator. */
  function buildLineItemProperties(uploaded) {
    const key = State.product.key;
    const country = (CFG.market && CFG.market.country) || "US";
    const coef = marketCoefficient(country);
    const perNapkin = unitPriceUSD(key, State.qty) * coef;
    const shape = State.product.shape === "square" ? "Square" : "Diamond";
    const productType = State.product.group + " " + shape; // "Cocktail Square" / "1/4 Fold Diamond" etc.
    // Finish: explicit ink / foil-with-colour.
    let finish;
    if (State.foil) {
      finish = "Foil " + ((State.foilColor && State.foilColor.name) || "Gold");
    } else {
      finish = "Ink";
    }
    const props = {};
    props["_Product Type"] = productType;
    props["_Napkin Color"] = State.napkinColor ? State.napkinColor.name : "";
    props["_Finish"] = finish;
    // Design colour: §A7 says "_Design Color OR _Pantone" — mutually exclusive.
    if (!State.foil) {
      if (State.designColorTab === "pantone") {
        props["_Pantone"] = State.pantone || "(to confirm)";
      } else if (State.designColorTab === "custom") {
        props["_Design Color"] = "Custom";
        props["_Ink Hex"] = State.customHex || ""; // extension — exact hex the customer picked
      } else {
        props["_Design Color"] =
          (State.designColor && State.designColor.name) || "Black";
      }
    }
    // Text + font (omit both keys when there's no text). Multiple boxes → Text is
    // the box contents joined " | "; _Text Font the per-box fonts in the same order.
    const filledTexts = State.texts.filter((t) => t.text.trim());
    if (filledTexts.length) {
      props["Text"] = filledTexts.map((t) => t.text).join(" | "); // unprefixed = shown to customer in cart/checkout (per §A7)
      props["_Text Font"] = filledTexts
        .map((t) => (t.font && t.font.family) || "")
        .join(", ");
    }
    props["_Has Image"] = State.logo ? "Yes" : "No";
    if (State.logo) props["_Artwork File"] = State.logo.name; // operational extension
    // NOTE: _Quantity intentionally OMITTED. The cart line's `quantity` field is the source
    // of truth — Shopify keeps it live if the customer edits qty in the cart. Storing it
    // again here would drift on edit. Backend B1 reads cart.line.quantity, not a property.
    props["_Unit Price (at add)"] = moneyUSD(perNapkin); // recorded snapshot, NOT the charged price (Shopify auto-discounts decide that per BUILD-PLAN-2026-05-20). Key is named "(at add)" so its staleness vs. a cart-edit is self-documenting.
    // Sample = the customer's INTENT at add-to-cart. Same staleness caveat as above.
    props["_Sample"] = State.sample && State.qty >= SAMPLE_MIN() ? "Yes" : "No";
    props["_Market"] = country;
    if (uploaded) {
      props["_Design File URL"] = uploaded.designUrl;
      props["_Proof File URL"] = uploaded.proofUrl;
    }
    // Vector Print Pipeline: the linked Design File URL is a true vector PDF
    // (generated + approved server-side). _Design Format is the v1/v2/vector
    // discriminator the backend reads (absent ⇒ legacy raster).
    if (CFG.vectorPipeline) props["_Design Format"] = "vector";
    // §A7: T&C tick = digital proof. Both timestamps reflect the SAME legal consent event.
    // Captured at modal-tick (openModal resets, change handler sets); read here at cart-add.
    // Fallback to "now" only if termsAcceptedAt is unset (defensive — should never happen
    // because the Add to Cart button stays disabled until termsInput is checked).
    const consentTs = State.termsAcceptedAt || new Date().toISOString();
    props["_Terms Accepted At"] = consentTs;
    props["_Design Approved At"] = consentTs;
    // Placement spec (extension) — lets the print team reproduce the layout deterministically.
    const spec = {};
    if (filledTexts.length)
      spec.texts = filledTexts.map((t) => ({
        ...t.box,
        content: t.text,
        font: (t.font && t.font.family) || "",
      }));
    if (State.logo && State.logoBox)
      spec.artwork = { ...State.logoBox, file: State.logo.name };
    const geo = NKD.geo.napkin(State.product);
    spec.imprintInches = geo.imprintIn; // 4.0 cocktail, 3.75 QF (fold-corner quadrant)
    spec.insets = geo.insets; // per-edge non-printable margin (inches); QF folds at 0
    if (geo.foldCorner) spec.foldCorner = geo.foldCorner;
    spec.units = "fractions-of-imprint";
    props["_Placement Spec"] = JSON.stringify(spec);
    props["_Source"] = "designer-v2";
    return props;
  }

  /* Post a single cart-add. Resolves with the response JSON on success; rejects with {status, body}
     on Shopify-side rejection (e.g. DRAFT variant → 422 "Product is unavailable"). */
  function postCartAdd(payload) {
    const url = (CFG.routes && CFG.routes.cartAdd) || "/cart/add.js";
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    }).then((r) => {
      return r.text().then((t) => {
        let body;
        try {
          body = t ? JSON.parse(t) : null;
        } catch {
          body = t;
        }
        if (!r.ok) {
          const err = new Error(
            "/cart/add.js " +
              r.status +
              ": " +
              (body && body.description
                ? body.description
                : typeof body === "string"
                  ? body
                  : ""),
          );
          err.status = r.status;
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  }

  function handleAddToCart(btn) {
    if (!State.texts.some((t) => t.text.trim()) && !State.logo) {
      toast("Add some text or upload artwork first.", "error");
      return;
    }
    if (!pickDesignerVariantId()) {
      toast(
        "No purchasable variant available on this product. Contact us to place the order.",
        "error",
      );
      console.error(
        "[napkin-designer] CFG.variants is empty or unusable:",
        CFG.variants,
      );
      return;
    }
    btn.disabled = true;
    const orig = btn.textContent;
    const setStatus = (s) => (btn.textContent = s);

    setStatus("Preparing your design…");
    // load the logo AND every design font in use before any render — the proof
    // PNG and print PDF rasterise the fallback face (and wrap on fallback metrics)
    // if a picked font isn't loaded at draw time.
    Promise.all([loadLogoImg(), ensureFontsLoaded(usedTextFonts())])
      .then((parts) => {
        // Vector Print Pipeline: the print PDF + proof were already generated AND
        // approved server-side at the gate (openModal → generate_vector). The
        // approved proof IS the print file — no client jsPDF, no re-upload here.
        if (CFG.vectorPipeline) {
          if (!State.vectorPrintUrl)
            throw new Error(
              "Please tap “Preview napkin” to generate your print file before adding to cart.",
            );
          return { __vector: true };
        }
        const logoImg = parts[0];
        // (1) proof PNG (also paints dom.exportCanvas so the modal preview stays consistent)
        renderToCanvas(dom.exportCanvas, NKD.geo.PROOF_PX, {
          bg: undefined,
          logoImg: logoImg,
        });
        setStatus("Rendering proof…");
        return renderProofPng(logoImg).then((proofBlob) => {
          setStatus("Rendering print file…");
          return renderPrintPdf(logoImg).then((printPdfBlob) => ({
            proofBlob,
            printPdfBlob,
            logoImg,
          }));
        });
      })
      .then((res) => {
        if (res && res.__vector)
          return {
            designUrl: State.vectorPrintUrl,
            proofUrl: State.vectorProofUrl,
          };
        setStatus("Uploading…");
        return uploadDesignFiles({
          printPdf: res.printPdfBlob,
          proof: res.proofBlob,
        });
      })
      .then((uploaded) => {
        setStatus("Adding to cart…");
        // ATOMIC items[] post per BUILD-PLAN-2026-05-20 §4.2: napkin + (foil) + (sample)
        // in a single /cart/add.js call. Shopify processes the array as one transaction —
        // either all lines add or none do. No partial cart states to clean up.
        const items = [
          {
            id: pickDesignerVariantId(),
            quantity: State.qty,
            properties: buildLineItemProperties(uploaded),
          },
        ];

        // Foil surcharge (separate line — auto-discounts only subtract, can't add).
        // Gated on a configured surcharge: foil is offered at no extra charge when
        // foil.surcharge_per_napkin is 0 (the live state as of 2026-06-19), so no
        // surcharge line is added and the now-inert "Foil Finish" Shopify product is
        // never referenced. Set foil.surcharge_per_napkin > 0 in the pricing config to
        // re-enable (requires the surcharge variant IDs to be wired in section settings).
        if (State.foil && FOIL_SURCHARGE() > 0) {
          const slot = State.product.key + "-" + State.product.shape;
          const foilId = Number((CFG.foilSurchargeVariants || {})[slot]);
          if (!Number.isFinite(foilId) || foilId <= 0) {
            // FAIL LOUD — better than silent revenue leak (0.40 × qty missing).
            // Surfaced to customer; surfaced to operator via console.error.
            console.error(
              "[napkin-designer] foil variant not configured for slot:",
              slot,
              "CFG.foilSurchargeVariants:",
              CFG.foilSurchargeVariants,
            );
            const err = new Error(
              "Foil pricing isn't fully configured yet — please contact us and we'll place your order.",
            );
            err.configError = true;
            throw err;
          }
          items.push({
            id: foilId,
            quantity: State.qty,
            properties: {
              "_Foil Color": (State.foilColor && State.foilColor.name) || "",
              "_Paired With": "Custom napkin design (above)",
            },
          });
        }

        // Physical sample ($50 flat, single unit, only at qty ≥ 1,000).
        // Guard against an unconfigured/blank sample variant ID — Number(undefined) === NaN
        // → posting NaN as `id` would 422 every time the customer opts in.
        const sampleId = Number(CFG.samplePhysicalVariantId);
        if (
          State.sample &&
          State.qty >= SAMPLE_MIN() &&
          Number.isFinite(sampleId) &&
          sampleId > 0
        ) {
          items.push({
            id: sampleId,
            quantity: 1,
            // Unprefixed keys = VISIBLE to the customer in cart/checkout/emails.
            // Important context for the $50 line that appears alongside the designer
            // line — otherwise customers wonder why a "Physical Sample" product is in
            // their cart at all. (Underscored sibling keys would render hidden.)
            properties: {
              "Linked to": "Custom napkin order (above)",
              Note: "Physical sample — ships before the full run for your approval.",
            },
          });
        }

        return postCartAdd({ items });
      })
      .then(() => {
        // (6) Route into the theme's CUSTOM mini-cart drawer instead of the native /cart
        // page (Debra/Keith 18 Jun: "brings them to the Shopify Native cart instead of the
        // mini cart"). The mini-cart is a custom developer build — window.PROJ.ajaxcart in
        // js_mini_cart.js — so we ONLY CALL its public API here and never modify it.
        // updateCart() re-fetches the cart section, updates the count bubble, AND slides the
        // drawer open in a single call (the same entry point the theme uses after every add).
        // The atomic items[] POST above is kept exactly as-is. If the mini-cart API isn't
        // present (e.g. the theme is swapped), fall back to the hard /cart redirect so
        // add-to-cart never dead-ends.
        const aj = window.PROJ && window.PROJ.ajaxcart;
        if (aj && typeof aj.updateCart === "function") {
          try {
            setStatus("Done — added to cart");
            // Close our proof modal (z-index 9999) BEFORE the mini-cart drawer (z-index
            // 9995) slides in — otherwise the drawer opens BEHIND the still-open modal and
            // the customer can't see it. closeModal() also restores body scroll (the modal
            // locks it). The design is already committed to the cart by this point, so
            // closeModal()'s in-flight-generation invalidation is a no-op here.
            // (iPhone report, 20 Jun: "mini cart opens behind the napkin preview".)
            closeModal();
            aj.updateCart(); // refresh contents + open the drawer (fire-and-forget)
            // The page stays put now (the old path navigated away), so restore the button.
            btn.disabled = false;
            btn.textContent = orig;
            return;
          } catch (err) {
            console.error(
              "[napkin-designer] mini-cart updateCart failed; falling back to /cart",
              err,
            );
          }
        }
        setStatus("Done — going to cart…");
        const cartUrl = (CFG.routes && CFG.routes.cart) || "/cart";
        window.location.href = cartUrl;
      })
      .catch((e) => {
        console.error("[napkin-designer] add-to-cart failed", e);
        const isCartErr = e && e.status >= 400 && e.status < 600;
        const isFriendly =
          e && (e.configError === true || e.stubBlocked === true);
        toast(
          isFriendly
            ? e.message // surface the human-readable message verbatim
            : isCartErr
              ? "Couldn't add to cart: " +
                (e.body && e.body.description
                  ? e.body.description
                  : "please try again")
              : e && e.message
                ? e.message
                : "Something went wrong preparing your design. Please try again.",
          "error",
        );
        btn.disabled = false;
        btn.textContent = orig;
      });
  }

  /* ==================================================================== boot */
  function boot() {
    if (!ROOT) return;
    renderShell();
    loadPricing()
      .then(() => {
        hydrateData();
        // pre-fetch the default design font (hydrateData seeds one text box with
        // DATA.fonts[0]) so the initial editor render isn't a fallback face.
        if (DATA.fonts && DATA.fonts[0]) loadFont(DATA.fonts[0]);
        refreshSteps();
        paintPreview();
      })
      .catch((e) => {
        console.error("[napkin-designer] pricing config failed to load", e);
        toast(
          "Pricing is taking a moment to load — refresh if it doesn't appear.",
          "error",
        );
      });
    // Repaint on viewport changes — but ONLY when the change actually alters the
    // napkin's computed display size. On mobile, scrolling fires `resize` (the address
    // bar showing/hiding), and a blind repaint would rebuild every layer on each scroll.
    // currentNapkinDisplaySize() is now scroll-stable on mobile (width-only), so a scroll
    // resize computes the same size and we skip the repaint; a real width/orientation
    // change (or a desktop height change) still changes the size and repaints.
    window.addEventListener(
      "resize",
      debounce(function () {
        if (currentNapkinDisplaySize() !== lastPaintSize) paintPreview();
      }, 150),
    );
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      (document.fonts && document.fonts.ready
        ? document.fonts.ready
        : Promise.resolve()
      ).then(boot);
    });
  } else {
    (document.fonts && document.fonts.ready
      ? document.fonts.ready
      : Promise.resolve()
    ).then(boot);
  }
})();
